import * as i0 from '@angular/core';
import { Injectable, InjectionToken, Inject, APP_INITIALIZER, NgModule } from '@angular/core';
import * as i1 from '@ngrx/store';
import { createAction, props, createFeatureSelector, createSelector, createReducer, on, StoreModule } from '@ngrx/store';
import * as i1$2 from '@ngrx/effects';
import { ofType, createEffect, EffectsModule } from '@ngrx/effects';
import { take, catchError, filter, switchMap, map, withLatestFrom } from 'rxjs/operators';
import { throwError, of } from 'rxjs';
import * as i1$1 from '@angular/common/http';
import { SortFields, TransactionGroupFilter } from '@usf/payment-types';
import { createEntityAdapter } from '@ngrx/entity';
import * as i2 from '@panamax/app-state';

/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable no-shadow */
var SortDirection;
(function (SortDirection) {
    SortDirection["ASC"] = "asc";
    SortDirection["DESC"] = "desc";
})(SortDirection || (SortDirection = {}));

const ACCOUNT_SUMMARY_ACTIONS = {
    getAccountSummary: createAction('[NgRxPayment - Account Balance] Get Account Balance From API', props()),
    getAccountSummarySuccess: createAction('[NgRxPayment - Account Balance] Get Account Balance From API Success', props()),
    getAccountSummaryFail: createAction('[NgRxPayment - Account Balance] Get Account Balance From API Failure', props()),
    resetAccountSummary: createAction('[NgRxPayment - Account Balance] Reset Account Summary'),
};

class AccountSummaryStoreService {
    constructor(store) {
        this.store = store;
    }
    getAccountSummary(customers) {
        this.store.dispatch(ACCOUNT_SUMMARY_ACTIONS.getAccountSummary({
            customers,
        }));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AccountSummaryStoreService, deps: [{ token: i1.Store }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AccountSummaryStoreService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AccountSummaryStoreService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.Store }]; } });

const AR_STATEMENT_ACTIONS = {
    getArStatements: createAction('[NgRxPayment - AR Statement] Get Statements From API', props()),
    getArStatementsSuccess: createAction('[NgRxPayment - AR Statement] Get Statements From API Success', props()),
    getArStatementsFail: createAction('[NgRxPayment - AR Statement] Get Statements From API Failure', props()),
    setArStatementsFilters: createAction('[NgRxPayment - AR Statement] Set Statements Filters', props()),
    setArStatementsSortOrder: createAction('[NgRxPayment - AR Statement] Set Statements Sort Order', props()),
    selectStatements: createAction('[NgRxPayment - AR Statement] Select Statements', props()),
    selectMaxStatements: createAction('[NgRxPayment - AR Statement] Select Max Statements', props()),
    clearSelectedInvoices: createAction('[NgRxPayment - AR Statement] Clear Statements selected'),
};

const PAYMENT_ACCOUNT_SUMMARY = 'payment-account-summary';
const PAYMENT_AR_STATEMENT = 'payment-ar-statement';
const PAYMENT_BANK_DETAILS = 'payment-bank-details';
const PAYMENT_ACH_FORM = 'payment-ach-form';
const PAYMENT_CUSTOMER_KEY = 'payment-customer';
const PAYMENT_DOMAIN_NAME = 'payment';
const PAYMENT_INVOICE = 'payment-invoice';
const PAYMENT_RECENT_CREDIT = 'payment-recent-credit';
const PAYMENT_STATUS_SUMMARY = 'payment-status-summary';
const PAYMENT_ACTIVITY_STATUS_SUMMARY = 'payment-activity-status-summary';
const PAYMENT_OPEN_INVOICES = 'open-invoices';
const PAYMENT_CURRENT = 'payment-current';
const PAYMENT_ACTIVITY = 'payment-activity';
const PAYMENT_METRIC = 'payment-metric';
const PAYMENT_STORAGE_KEYS = [
    PAYMENT_ACCOUNT_SUMMARY,
    PAYMENT_AR_STATEMENT,
    PAYMENT_BANK_DETAILS,
    PAYMENT_ACH_FORM,
    PAYMENT_CUSTOMER_KEY,
    PAYMENT_INVOICE,
    PAYMENT_RECENT_CREDIT,
    PAYMENT_STATUS_SUMMARY,
    PAYMENT_OPEN_INVOICES,
    PAYMENT_CURRENT,
    PAYMENT_METRIC,
];

const selectArStatementState = createFeatureSelector(PAYMENT_AR_STATEMENT);
const selectArStatements = createSelector(selectArStatementState, (arStatementState) => arStatementState);
const selectArStatementsPageNumber = createSelector(selectArStatementState, (arStatementState) => arStatementState.pageNum);
const arStatementSelectors = {
    selectArStatements,
    selectArStatementsPageNumber,
};

class ArStatementStoreService {
    constructor(store) {
        this.store = store;
    }
    fetchArStatements(pageNum, pageSize) {
        this.store.dispatch(AR_STATEMENT_ACTIONS.getArStatements({
            pageNum,
            pageSize,
        }));
    }
    getArStatements$() {
        return this.store.select(arStatementSelectors.selectArStatements);
    }
    setArStatementFilters(filters, pageSize) {
        this.store.dispatch(AR_STATEMENT_ACTIONS.setArStatementsFilters({
            filters,
            pageSize,
        }));
    }
    setArStatementSort(sortOrder, pageSize) {
        this.store.dispatch(AR_STATEMENT_ACTIONS.setArStatementsSortOrder({
            pageSize,
            sortOrder,
        }));
    }
    selectArStatements(statementsSelected) {
        this.store.dispatch(AR_STATEMENT_ACTIONS.selectStatements({ statementsSelected }));
    }
    selectMaxStatements(maxSelection) {
        this.store.dispatch(AR_STATEMENT_ACTIONS.selectMaxStatements({ maxSelection }));
    }
    clearSelectedArStatements() {
        this.store.dispatch(AR_STATEMENT_ACTIONS.clearSelectedInvoices());
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: ArStatementStoreService, deps: [{ token: i1.Store }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: ArStatementStoreService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: ArStatementStoreService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.Store }]; } });

const BANK_ACTIONS = {
    getBankDetails: createAction('[NgRxPayment - Payment Bank] Get Bank details From API', props()),
    getBankDetailsSuccess: createAction('[NgRxPayment - Payment Bank] Get Bank details From API Success', props()),
    getBankDetailsFail: createAction('[NgRxPayment - Payment Bank] Get Bank details From API Failure', props()),
    getUniqueBankDetails: createAction('[NgRxPayment - Payment Bank] Get Unique Bank details From API'),
    getUniqueBankDetailsSuccess: createAction('[NgRxPayment - Payment Bank] Get Unique Bank details From API Success', props()),
    getUniqueBankDetailsFail: createAction('[NgRxPayment - Payment Bank] Get Unique Bank details From API Failure', props()),
    setBankAccountsForPaying: createAction('[NgRxPayment - Payment Bank] Set Payment bank Accounts', props()),
    addCustomerBankAccount: createAction('[NgRxPayment - Payment Bank] Add Customer Bank Account to API', props()),
    postBankAccounts: createAction('[NgRxPayment - Payment Bank] Post Bank Accounts to API', props()),
    postBankAccountsSuccess: createAction('[NgRxPayment - Payment Bank] Post Bank Accounts to API Success', props()),
    postBankAccountsFail: createAction('[NgRxPayment - Payment Bank] Post Bank Accounts to API Failure', props()),
    deleteBankAccount: createAction('[NgRxPayment - Payment Bank] Delete Bank Account to API', props()),
    deleteBankAccountSuccess: createAction('[NgRxPayment - Payment Bank] Delete Bank Account to API Success', props()),
    deleteBankAccountFail: createAction('[NgRxPayment - Payment Bank] Delete Bank Account to API Failure', props()),
    getEligibleADCustomers: createAction('[NgRxPayment - Payment Bank] Get Eligible AD Customers From API'),
    getEligibleADCustomersSuccess: createAction('[NgRxPayment - Payment Bank] Get Eligible AD Customers From API Success', props()),
    getEligibleADCustomersFail: createAction('[NgRxPayment - Payment Bank] Get Eligible AD Customers From API Failure', props()),
    clearEligibleADCustomers: createAction('[NgRxPayment - Payment Bank] Clear Eligible AD Customers'),
    addCustomerBankAccountSuccess: createAction('[NgRxPayment - Payment Bank] Add Customer Bank Account to API Success', props()),
    addCustomerBankAccountFail: createAction('[NgRxPayment - Payment Bank] Add Customer Bank Account to API Failure', props()),
    clearError: createAction('[NgRxPayment - Payment Bank ] Clear Error'),
    postADBankAccounts: createAction('[NgRxPayment - Payment Bank] Post AD Bank Accounts to API', props()),
    postADBankAccountsSuccess: createAction('[NgRxPayment - Payment Bank] Post AD Bank Accounts to API Success', props()),
    postADBankAccountsFail: createAction('[NgRxPayment - Payment Bank] Post AD Bank Accounts to API Failure', props()),
};

const selectBankState = createFeatureSelector(PAYMENT_BANK_DETAILS);
const selectBank = createSelector(selectBankState, (bankDetailsState) => bankDetailsState);
const bankSelectors = {
    selectBank,
};

class BankStoreService {
    constructor(store) {
        this.store = store;
    }
    fetchBanksDetails(customers, primaryOnly = true) {
        this.store.dispatch(BANK_ACTIONS.getBankDetails({ customers, primaryOnly }));
    }
    fetchUniqueBankAccounts() {
        this.store.dispatch(BANK_ACTIONS.getUniqueBankDetails());
    }
    getEligibleADCustomers() {
        this.store.dispatch(BANK_ACTIONS.getEligibleADCustomers());
    }
    clearEligibleCustomers() {
        this.store.dispatch(BANK_ACTIONS.clearEligibleADCustomers());
    }
    getBanksDetails$() {
        return this.store.select(bankSelectors.selectBank);
    }
    addCustomerBankAccount(newCustomerBankAccount) {
        this.store.dispatch(BANK_ACTIONS.addCustomerBankAccount({ newCustomerBankAccount }));
    }
    clearError() {
        this.store.dispatch(BANK_ACTIONS.clearError());
    }
    setBanksToPay(bankAccounts) {
        this.store.dispatch(BANK_ACTIONS.setBankAccountsForPaying({
            bankAccounts,
        }));
    }
    postBankAccount(newCustomerBankAccount) {
        this.store.dispatch(BANK_ACTIONS.postBankAccounts({
            newCustomerBankAccount,
        }));
    }
    postADBankAccount(newADBankAccount) {
        this.store.dispatch(BANK_ACTIONS.postADBankAccounts({
            newADBankAccount,
        }));
    }
    removeBankAccount(bankID) {
        this.store.dispatch(BANK_ACTIONS.deleteBankAccount({
            bankID,
        }));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: BankStoreService, deps: [{ token: i1.Store }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: BankStoreService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: BankStoreService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.Store }]; } });

const ACH_FORM_ACTIONS = {
    getGeneratedACHForm: createAction('[NgRxPayment - ACH Form] Get Generated ACH Form From API', props()),
    getGeneratedACHFormSuccess: createAction('[NgRxPayment - ACH Form] Get Generated ACH Form From API Success', props()),
    getGeneratedACHFormFail: createAction('[NgRxPayment - ACH Form] Get Generated ACH Form From API Failure', props()),
    clearError: createAction('[NgRxPayment - Payment Bank ] Clear Error'),
};

const selectAchFormState = createFeatureSelector(PAYMENT_ACH_FORM);
const selectAchForm = createSelector(selectAchFormState, (achFormState) => achFormState);
const achFormSelectors = {
    selectAchForm,
};

class AchFormStoreService {
    constructor(store) {
        this.store = store;
    }
    getGeneratedACHForm(auditID) {
        this.store.dispatch(ACH_FORM_ACTIONS.getGeneratedACHForm({ auditID }));
    }
    getAchFormState$() {
        return this.store.select(achFormSelectors.selectAchForm);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AchFormStoreService, deps: [{ token: i1.Store }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AchFormStoreService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AchFormStoreService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.Store }]; } });

const INVOICE_ACTIONS = {
    clearSelectedInvoices: createAction('[NgRxPayment - Invoice] Clear Selected Invoices'),
    selectInvoices: createAction('[NgRxPayment - Invoice] Select Invoices', (ids, translatedIds = {}) => ({
        ids,
        translatedIds,
    })),
    selectMaxInvoices: createAction('[NgRxPayment - Invoice] Select Max Invoices', props()),
    getInvoices: createAction('[NgRxPayment - Invoice] Get Invoices From API', props()),
    getInvoicesSuccess: createAction('[NgRxPayment - Invoice] Get Invoices From API Success', props()),
    getInvoicesFail: createAction('[NgRxPayment - Invoice] Get Invoices From API Failure', props()),
    setInvoiceFilters: createAction('[NgRxPayment - Invoice] Set Invoice Filters', props()),
    setInvoiceSortOrder: createAction('[NgRxPayment - Invoice] Set Invoice Sort Order', props()),
    updateInvoiceAfterPay: createAction('[NgRxPayment - Invoice] Update Invoices', props()),
    updateInvoice: createAction('[NgRxPayment - Invoice] Update Selected Invoices', props()),
    setInitialSelectedInvoices: createAction('[NgRxPayment - Invoice] Set Initial Selected Invoices'),
};

const OPEN_INVOICES_ACTIONS = {
    getOpenInvoices: createAction('[NgRxPayment - Open Invoices] Get Open Invoices from API', props()),
    getOpenInvoicesSuccess: createAction('[NgRxPayment - Open Invoices] Get Open Invoices from API Success', props()),
    getOpenInvoicesFail: createAction('[NgRxPayment - Open Invoices] Get Open Invoices from API Failure', props()),
};

const STATUS_SUMMARY_ACTIONS = {
    getStatusSummary: createAction('[NgRxPayment - Status Summary] Get Status from API', props()),
    reloadStatusSummary: createAction('[NgRxPayment - Status Summary] Revalidate Status from API', props()),
    getStatusSummarySuccess: createAction('[NgRxPayment - Status Summary] Get Status from API Success', props()),
    getStatusSummaryFail: createAction('[NgRxPayment - Status Summary] Get Status from API Failure', props()),
    getPaymentActivityStatusSummary: createAction('[NgRxPayment - Status Summary] Get Summary of Payment Activity from API', props()),
    reloadPaymentActivityStatusSummary: createAction('[NgRxPayment - Status Summary] Revalidate Summary of Payment Activity from API'),
    getPaymentActivityStatusSummarySuccess: createAction('[NgRxPayment - Status Summary] Get Summary of Payment Activity Success', props()),
    getPaymentActivityStatusSummaryFail: createAction('[NgRxPayment - Status Summary] Get Summary of Payment Activity Failure', props()),
};

const selectInvoiceState = createFeatureSelector(PAYMENT_INVOICE);
const selectInvoices = createSelector(selectInvoiceState, (invoiceState) => invoiceState);
const selectInvoicesPageNumber = createSelector(selectInvoiceState, (invoiceState) => invoiceState.pageNum);
const invoicesSelectors = {
    selectInvoices,
    selectInvoicesPageNumber,
};

class InvoiceStoreService {
    constructor(store) {
        this.store = store;
    }
    clearSelectedInvoices() {
        this.store.dispatch(INVOICE_ACTIONS.clearSelectedInvoices());
    }
    fetchInvoices(pageNum, restrictToOpen = false, pageSize, keepSelectedInvoices = false) {
        this.store.dispatch(INVOICE_ACTIONS.getInvoices({
            pageNum,
            pageSize,
            restrictToOpen,
            keepSelectedInvoices,
        }));
    }
    getInvoices$() {
        return this.store.select(invoicesSelectors.selectInvoices);
    }
    selectInvoices(invoiceIds, translatedIds = {}) {
        this.store.dispatch(INVOICE_ACTIONS.selectInvoices(invoiceIds, translatedIds));
    }
    selectMaxInvoices(maxSelection) {
        this.store.dispatch(INVOICE_ACTIONS.selectMaxInvoices({ maxSelection }));
    }
    setInvoiceFilters(filters, restrictToOpen = false, shouldDispatchStatusSummary = true, pageSize) {
        this.store.dispatch(INVOICE_ACTIONS.setInvoiceFilters({
            filters,
            restrictToOpen,
            pageSize,
        }));
        if (shouldDispatchStatusSummary) {
            this.store.dispatch(STATUS_SUMMARY_ACTIONS.getStatusSummary({ filters, restrictToOpen }));
        }
    }
    reloadStatusSummary(restrictToOpen) {
        this.store.dispatch(STATUS_SUMMARY_ACTIONS.reloadStatusSummary({ restrictToOpen }));
    }
    fetchOpenInvoices(filters, restrictToOpen = true) {
        this.store.dispatch(OPEN_INVOICES_ACTIONS.getOpenInvoices({ filters, restrictToOpen }));
    }
    setInvoiceSort(sortOrder, restrictToOpen = false, pageSize) {
        this.store.dispatch(INVOICE_ACTIONS.setInvoiceSortOrder({
            sortOrder,
            restrictToOpen,
            pageSize,
        }));
    }
    setInitialSelectedInvoices() {
        this.store.dispatch(INVOICE_ACTIONS.setInitialSelectedInvoices());
    }
    updateInvoice(invoiceId, data) {
        this.store.dispatch(INVOICE_ACTIONS.updateInvoice({
            invoiceId,
            data,
        }));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: InvoiceStoreService, deps: [{ token: i1.Store }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: InvoiceStoreService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: InvoiceStoreService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.Store }]; } });

const PAYMENT_CUSTOMER_ACTIONS = {
    customerUpdated: createAction('[NgRxPayment - Payment Customer] Customer Selection Updated', props()),
};

const selectPaymentCustomerState = createFeatureSelector(PAYMENT_CUSTOMER_KEY);
const selectPaymentCustomer = createSelector(selectPaymentCustomerState, (paymentCustomerState) => paymentCustomerState);
const paymentCustomerSelectors = {
    selectPaymentCustomer,
};

class PaymentCustomerStoreService {
    constructor(store) {
        this.store = store;
    }
    customerUpdated(customers) {
        this.store.dispatch(PAYMENT_CUSTOMER_ACTIONS.customerUpdated({
            customers,
        }));
    }
    getSelectedCustomer$() {
        return this.store.select(paymentCustomerSelectors.selectPaymentCustomer);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: PaymentCustomerStoreService, deps: [{ token: i1.Store }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: PaymentCustomerStoreService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: PaymentCustomerStoreService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.Store }]; } });

const PAYMENT_METRIC_ACTIONS = {
    getMetricSummary: createAction('[NgRxPayment - Payment Metric] Get Metric Summary to API', props()),
    getMetricSummarySuccess: createAction('[NgRxPayment - Payment Metric] Get Metric Summary to API Success', props()),
    getMetricSummaryFail: createAction('[NgRxPayment - Payment Metric] Get Metric Summary to API Failure', props()),
    getMetricDetails: createAction('[NgRxPayment - Payment Metric] Get Metric Details to API', props()),
    getMetricDetailsSuccess: createAction('[NgRxPayment - Payment Metric] Get Metric Details to API Success', props()),
    getMetricDetailsFail: createAction('[NgRxPayment - Payment Metric] Get Metric Details to API Failure', props()),
};

const PAYMENT_ACTIONS = {
    clearError: createAction('[NgRxPayment - Submit Payment] Clear Error'),
    clearPayments: createAction('[NgRxPayment - Submit Payment] Clear Payments'),
    clearPaymentDetails: createAction('[NgRxPayment - Submit Payment] Clear Payment Details'),
    submitPayment: createAction('[NgRxPayment - Submit Payment] Submit Payment to API', props()),
    submitPaymentSuccess: createAction('[NgRxPayment - Submit Payment] Submit Payment to API Success', props()),
    submitPaymentFail: createAction('[NgRxPayment - Submit Payment] Submit Payment to API Failure', props()),
    getPaymentDetails: createAction('[NgRxPayment - Payment Details] Get Payment Details to API', props()),
    getPaymentDetailsSuccess: createAction('[NgRxPayment - Payment Details] Get Payment Details to API Success', props()),
    getPaymentDetailsFail: createAction('[NgRxPayment - Payment Details] Get Payment Details to API Failure', props()),
    cancelPayment: createAction('[NgRxPayment - Cancel Payment] Post Cancel Payment to API', props()),
    cancelPaymentSuccess: createAction('[NgRxPayment - Cancel Payment] Post Cancel Payment to API Success'),
    cancelPaymentFail: createAction('[NgRxPayment - Cancel Payment] Post Cancel Payment to API Failure', props()),
    clearCancellingFlag: createAction('[NgRxPayment - Cancel Payment] Clear Cancelling Flag'),
    getPaymentActivity: createAction('[NgRxPayment - Payment Activity] Get Payment Activity to API', props()),
    getPaymentActivitySuccess: createAction('[NgRxPayment - Payment Activity] Get Payment Activity to API Success', props()),
    getPaymentActivityFail: createAction('[NgRxPayment - Payment Activity] Get Payment Activity to API Failure', props()),
    setPaymentActivityFilters: createAction('[NgRxPayment - Payment Activity] Set Payment Activity Filters', props()),
    setPaymentActivitySortOrder: createAction('[NgRxPayment - Payment Activity] Set Payment Activity Sort Order', props()),
};

/* eslint-disable @typescript-eslint/naming-convention */
const DefaultPageSize = 50;
const MAX_STATEMENT_SELECTION = 75;
const MAX_INVOICE_SELECTION = 75;

class PaymentStoreService {
    constructor(store) {
        this.store = store;
    }
    clearError() {
        this.store.dispatch(PAYMENT_ACTIONS.clearError());
    }
    clearPayments() {
        this.store.dispatch(PAYMENT_ACTIONS.clearPayments());
    }
    clearPaymentDetails() {
        this.store.dispatch(PAYMENT_ACTIONS.clearPaymentDetails());
    }
    submitPayment(scheduledPaymentDate) {
        this.store.dispatch(PAYMENT_ACTIONS.submitPayment({ scheduledPaymentDate }));
    }
    getPaymentDetails(paymentActivityID) {
        this.store.dispatch(PAYMENT_ACTIONS.getPaymentDetails({ paymentActivityID }));
    }
    cancelPayment(paymentActivityID) {
        this.store.dispatch(PAYMENT_ACTIONS.cancelPayment({ paymentActivityID }));
    }
    clearCancellingFlag() {
        this.store.dispatch(PAYMENT_ACTIONS.clearCancellingFlag());
    }
    getPaymentActivity(pageNum, pageSize = DefaultPageSize, headerOnly = true) {
        this.store.dispatch(PAYMENT_ACTIONS.getPaymentActivity({
            pageNum,
            pageSize,
            headerOnly,
        }));
    }
    setPaymentActivityFilters(filters, shouldDispatchStatusSummary = true, pageSize = DefaultPageSize) {
        this.store.dispatch(PAYMENT_ACTIONS.setPaymentActivityFilters({
            filters,
            pageSize,
        }));
        if (shouldDispatchStatusSummary) {
            this.store.dispatch(STATUS_SUMMARY_ACTIONS.getPaymentActivityStatusSummary({ filters }));
        }
    }
    reloadStatusSummary() {
        this.store.dispatch(STATUS_SUMMARY_ACTIONS.reloadPaymentActivityStatusSummary());
    }
    setPaymentActivitySort(sortOrder, pageSize = DefaultPageSize) {
        this.store.dispatch(PAYMENT_ACTIONS.setPaymentActivitySortOrder({
            sortOrder,
            pageSize,
        }));
    }
    getMetricSummary(customerNumbers) {
        this.store.dispatch(PAYMENT_METRIC_ACTIONS.getMetricSummary({ customerNumbers }));
    }
    getMetricDetails(customerNumbers) {
        this.store.dispatch(PAYMENT_METRIC_ACTIONS.getMetricDetails({ customerNumbers }));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: PaymentStoreService, deps: [{ token: i1.Store }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: PaymentStoreService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: PaymentStoreService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.Store }]; } });

const RECENT_CREDIT_ACTIONS = {
    getRecentCredits: createAction('[NgRxPayment - Recent Credit] Get Recent Credits From API', props()),
    getRecentCreditsSuccess: createAction('[NgRxPayment - Recent Credit] Get Recent Credits From API Success', props()),
    getRecentCreditsFail: createAction('[NgRxPayment - Recent Credit] Get Recent Credits From API Failure', props()),
};

const selectRecentCreditState = createFeatureSelector(PAYMENT_RECENT_CREDIT);
const selectRecentCredits = createSelector(selectRecentCreditState, (recentCreditState) => recentCreditState);
const recentCreditSelectors = {
    selectRecentCredits,
};

class RecentCreditStoreService {
    constructor(store) {
        this.store = store;
    }
    fetchRecentCredits(customers) {
        this.store.dispatch(RECENT_CREDIT_ACTIONS.getRecentCredits({
            customers,
        }));
    }
    getRecentCredits$() {
        return this.store.select(recentCreditSelectors.selectRecentCredits);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: RecentCreditStoreService, deps: [{ token: i1.Store }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: RecentCreditStoreService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: RecentCreditStoreService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.Store }]; } });

const SHARE_EMAIL_ACTIONS = {
    sharePaymentConfirmationViaEmail: createAction('[NgRxTrace - File Share] Share Payment Confirmation Via Email', props()),
    sharePaymentConfirmationViaEmailSuccess: createAction('[NgRxPayment - File Share] Share Payment Confirmation Success', props()),
    sharePaymentConfirmationViaEmailFail: createAction('[NgRxPayment - File Share] Share Payment Confirmation Failure', props()),
};

class SharePaymentsEmailStoreService {
    constructor(store, actionSubject) {
        this.store = store;
        this.actionSubject = actionSubject;
    }
    sharePaymentConfirmation(sharePaymentConfirmationViaEmail) {
        this.store.dispatch(SHARE_EMAIL_ACTIONS.sharePaymentConfirmationViaEmail({
            data: sharePaymentConfirmationViaEmail,
        }));
    }
    sharePaymentConfirmationSuccess$() {
        return this.actionSubject.pipe(ofType(SHARE_EMAIL_ACTIONS.sharePaymentConfirmationViaEmailSuccess), take(1));
    }
    sharePaymentConfirmationFail$() {
        return this.actionSubject.pipe(ofType(SHARE_EMAIL_ACTIONS.sharePaymentConfirmationViaEmailFail), take(1));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: SharePaymentsEmailStoreService, deps: [{ token: i1.Store }, { token: i1.ActionsSubject }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: SharePaymentsEmailStoreService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: SharePaymentsEmailStoreService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.Store }, { type: i1.ActionsSubject }]; } });

const selectAccountSummaryState = createFeatureSelector(PAYMENT_ACCOUNT_SUMMARY);
const selectAccountSummary = createSelector(selectAccountSummaryState, (accountSummaryState) => accountSummaryState);
const accountSummarySelectors = {
    selectAccountSummary,
};

const selectOpenInvoicesState = createFeatureSelector(PAYMENT_OPEN_INVOICES);
const selectOpenInvoices = createSelector(selectOpenInvoicesState, (openInvoicesState) => openInvoicesState);
const openInvoicesSelectors = {
    selectOpenInvoices,
};

const selectPaymentState = createFeatureSelector(PAYMENT_CURRENT);
const selectPayment = createSelector(selectPaymentState, (paymentState) => paymentState);
const selectPaymentActivityState = createFeatureSelector(PAYMENT_ACTIVITY);
const selectPaymentActivity = createSelector(selectPaymentActivityState, (paymentActivityState) => paymentActivityState);
const selectPaymentMetricState = createFeatureSelector(PAYMENT_METRIC);
const selectPaymentMetric = createSelector(selectPaymentMetricState, (paymentMetricState) => paymentMetricState);
const paymentSelectors = {
    selectPayment,
    selectPaymentActivity,
    selectPaymentMetricState,
};

const selectStatusSummaryState = createFeatureSelector(PAYMENT_STATUS_SUMMARY);
const selectPaymentActivityStatusSummaryState = createFeatureSelector(PAYMENT_ACTIVITY_STATUS_SUMMARY);
const selectPaymentActivityStatusSummary = createSelector(selectPaymentActivityStatusSummaryState, (statusSummaryState) => statusSummaryState);
const selectStatusSummary = createSelector(selectStatusSummaryState, (statusSummaryState) => statusSummaryState);
const statusSummarySelectors = {
    selectStatusSummary,
    selectPaymentActivityStatusSummary,
};

/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable no-shadow */
var InvoiceFilterIds;
(function (InvoiceFilterIds) {
    InvoiceFilterIds["CustomerPo"] = "customerPO";
    InvoiceFilterIds["Customers"] = "customers";
    InvoiceFilterIds["ExcludeInvoices"] = "excludeInvoices";
    InvoiceFilterIds["FromIssueDate"] = "fromIssueDate";
    InvoiceFilterIds["IssueDate"] = "issue-date";
    InvoiceFilterIds["Status"] = "status";
    InvoiceFilterIds["ToIssueDate"] = "toIssueDate";
    InvoiceFilterIds["TransactionGroup"] = "transactionGroupFilter";
    InvoiceFilterIds["TransactionNumber"] = "transactionNumber";
    InvoiceFilterIds["NeedISR"] = "needISR";
    InvoiceFilterIds["OrderNumber"] = "orderNumber";
})(InvoiceFilterIds || (InvoiceFilterIds = {}));

const NGRX_PAYMENT_LIB_CONFIG = new InjectionToken('NGRX_PAYMENT_LIB_CONFIG');

class PaymentDataHttpClientService {
    constructor(config, http) {
        this.config = config;
        this.http = http;
    }
    /**
     * @param path relative path of the resource
     * @param params optional additional HttpParams
     */
    get(path, params) {
        const url = `${this.config.paymentDomainApiUrl}/${path}`;
        return this.http
            .get(url, {
            params,
        })
            .pipe(catchError(this.handleError));
    }
    /**
     * @param path relative path of the resource
     * @param body the body of the put call
     * @param params optional additional HttpParams
     */
    put(path, body, params) {
        const url = `${this.config.paymentDomainApiUrl}/${path}`;
        return this.http
            .put(url, body || {}, {
            params,
        })
            .pipe(catchError(this.handleError));
    }
    /**
     * @param path relative path of the resource
     * @param body the body of the post call
     * @param params optional additional HttpParams
     */
    post(path, body, params) {
        const url = `${this.config.paymentDomainApiUrl}/${path}`;
        return this.http
            .post(url, body || {}, {
            params,
        })
            .pipe(catchError(this.handleError));
    }
    /**
     * @param path relative path of the resource
     * @param body the body of the delete call
     */
    delete(path, body) {
        const url = `${this.config.paymentDomainApiUrl}/${path}`;
        return this.http
            .request('delete', url, { body })
            .pipe(catchError(this.handleError));
    }
    /**
     * @param error - error response received from the api request
     * @returns empty Observable
     */
    handleError(error) {
        console.error('http error', error);
        return throwError(error);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: PaymentDataHttpClientService, deps: [{ token: NGRX_PAYMENT_LIB_CONFIG }, { token: i1$1.HttpClient }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: PaymentDataHttpClientService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: PaymentDataHttpClientService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [NGRX_PAYMENT_LIB_CONFIG]
                }] }, { type: i1$1.HttpClient }]; } });

class BankHttpClientService {
    constructor(config, http) {
        this.config = config;
        this.http = http;
    }
    /**
     * @param path relative path of the resource
     * @param params optional additional HttpParams
     */
    get(path, params) {
        const url = `${this.config.paymentBankDomainApiUrl}/${path}`;
        return this.http
            .get(url, {
            params,
        })
            .pipe(catchError(this.handleError));
    }
    /**
     * @param path relative path of the resource
     * @param body the body of the put call
     * @param params optional additional HttpParams
     */
    put(path, body, params) {
        const url = `${this.config.paymentBankDomainApiUrl}/${path}`;
        return this.http
            .put(url, body || {}, {
            params,
        })
            .pipe(catchError(this.handleError));
    }
    /**
     * @param path relative path of the resource
     * @param body the body of the post call
     * @param params optional additional HttpParams
     */
    post(path, body, params) {
        const url = `${this.config.paymentBankDomainApiUrl}/${path}`;
        return this.http
            .post(url, body || {}, {
            params,
        })
            .pipe(catchError(this.handleError));
    }
    /**
     * @param path relative path of the resource
     * @param body the body of the delete call
     */
    delete(path, body) {
        const url = `${this.config.paymentBankDomainApiUrl}/${path}`;
        return this.http
            .request('delete', url, { body })
            .pipe(catchError(this.handleError));
    }
    /**
     * @param error - error response received from the api request
     * @returns empty Observable
     */
    handleError(error) {
        console.error('http error', error);
        return throwError(error);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: BankHttpClientService, deps: [{ token: NGRX_PAYMENT_LIB_CONFIG }, { token: i1$1.HttpClient }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: BankHttpClientService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: BankHttpClientService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [NGRX_PAYMENT_LIB_CONFIG]
                }] }, { type: i1$1.HttpClient }]; } });

class PaymentHttpClientService {
    constructor(config, http) {
        this.config = config;
        this.http = http;
    }
    /**
     * @param path relative path of the resource
     * @param params optional additional HttpParams
     */
    get(path, params) {
        const url = `${this.config.paymentPayDomainApiUrl}/${path}`;
        return this.http
            .get(url, {
            params,
        })
            .pipe(catchError(this.handleError));
    }
    /**
     * @param path relative path of the resource
     * @param body the body of the post call
     * @param params optional additional HttpParams
     */
    post(path, body, params) {
        const url = `${this.config.paymentPayDomainApiUrl}/${path}`;
        return this.http
            .post(url, body || {}, {
            params,
        })
            .pipe(catchError(this.handleError));
    }
    /**
     * @param error - error response received from the api request
     * @returns empty Observable
     */
    handleError(error) {
        console.error('http error', error);
        return throwError(error);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: PaymentHttpClientService, deps: [{ token: NGRX_PAYMENT_LIB_CONFIG }, { token: i1$1.HttpClient }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: PaymentHttpClientService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: PaymentHttpClientService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [NGRX_PAYMENT_LIB_CONFIG]
                }] }, { type: i1$1.HttpClient }]; } });

class PaymentMetricHttpClientService {
    constructor(config, http) {
        this.config = config;
        this.http = http;
    }
    /**
     * @param path relative path of the resource
     * @param params optional additional HttpParams
     */
    get(path, params) {
        const url = `${this.config.paymentMetricsDomainApiUrl}/${path}`;
        return this.http
            .get(url, {
            params,
        })
            .pipe(catchError(this.handleError));
    }
    /**
     * @param path relative path of the resource
     * @param body the body of the put call
     * @param params optional additional HttpParams
     */
    put(path, body, params) {
        const url = `${this.config.paymentMetricsDomainApiUrl}/${path}`;
        return this.http
            .put(url, body || {}, {
            params,
        })
            .pipe(catchError(this.handleError));
    }
    /**
     * @param path relative path of the resource
     * @param body the body of the post call
     * @param params optional additional HttpParams
     */
    post(path, body, params) {
        const url = `${this.config.paymentMetricsDomainApiUrl}/${path}`;
        return this.http
            .post(url, body || {}, {
            params,
        })
            .pipe(catchError(this.handleError));
    }
    /**
     * @param path relative path of the resource
     * @param body the body of the delete call
     */
    delete(path, body) {
        const url = `${this.config.paymentMetricsDomainApiUrl}/${path}`;
        return this.http
            .request('delete', url, { body })
            .pipe(catchError(this.handleError));
    }
    /**
     * @param error - error response received from the api request
     * @returns empty Observable
     */
    handleError(error) {
        console.error('http error', error);
        return throwError(error);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: PaymentMetricHttpClientService, deps: [{ token: NGRX_PAYMENT_LIB_CONFIG }, { token: i1$1.HttpClient }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: PaymentMetricHttpClientService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: PaymentMetricHttpClientService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [NGRX_PAYMENT_LIB_CONFIG]
                }] }, { type: i1$1.HttpClient }]; } });

class ArStatementsHttpClientService {
    constructor(config, http) {
        this.config = config;
        this.http = http;
    }
    /**
     * @param path relative path of the resource
     * @param params optional additional HttpParams
     */
    get(path, params) {
        const url = `${this.config.paymentArStatementsDomainApiUrl}/${path}`;
        return this.http
            .get(url, {
            params,
        })
            .pipe(catchError(this.handleError));
    }
    /**
     * @param path relative path of the resource
     * @param body the body of the put call
     * @param params optional additional HttpParams
     */
    put(path, body, params) {
        const url = `${this.config.paymentArStatementsDomainApiUrl}/${path}`;
        return this.http
            .put(url, body || {}, {
            params,
        })
            .pipe(catchError(this.handleError));
    }
    /**
     * @param path relative path of the resource
     * @param body the body of the post call
     * @param params optional additional HttpParams
     */
    post(path, body, params) {
        const url = `${this.config.paymentArStatementsDomainApiUrl}/${path}`;
        return this.http
            .post(url, body || {}, {
            params,
        })
            .pipe(catchError(this.handleError));
    }
    /**
     * @param path relative path of the resource
     * @param body the body of the delete call
     */
    delete(path, body) {
        const url = `${this.config.paymentArStatementsDomainApiUrl}/${path}`;
        return this.http
            .request('delete', url, { body })
            .pipe(catchError(this.handleError));
    }
    /**
     * @param error - error response received from the api request
     * @returns empty Observable
     */
    handleError(error) {
        console.error('http error', error);
        return throwError(error);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: ArStatementsHttpClientService, deps: [{ token: NGRX_PAYMENT_LIB_CONFIG }, { token: i1$1.HttpClient }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: ArStatementsHttpClientService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: ArStatementsHttpClientService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [NGRX_PAYMENT_LIB_CONFIG]
                }] }, { type: i1$1.HttpClient }]; } });

class PaymentApiService {
    constructor(serviceHandler, bankServiceHandler, paymentServiceHandler, paymentMetricHandler, arStatementServiceHandler) {
        this.serviceHandler = serviceHandler;
        this.bankServiceHandler = bankServiceHandler;
        this.paymentServiceHandler = paymentServiceHandler;
        this.paymentMetricHandler = paymentMetricHandler;
        this.arStatementServiceHandler = arStatementServiceHandler;
    }
    getAccountSummary(customers) {
        return this.serviceHandler
            .post('accountSummary', customers.length ? { customers } : null)
            .pipe(filter((resp) => !!resp), catchError((error) => throwError(error)));
    }
    getArStatementsUsingSortAndFilter(pageNum, sort, filters, pageSize = DefaultPageSize) {
        const body = {
            ...this.mapBodyFilters(filters),
            pageNum,
            pageSize,
            sort,
        };
        return this.arStatementServiceHandler
            .post('arStatements', body)
            .pipe(filter((resp) => !!resp), catchError((error) => throwError(error)));
    }
    getInvoicesUsingSortAndFilter(pageNum, sort, filters, pageSize = DefaultPageSize, restrictToOpen = false) {
        const body = {
            needISR: false,
            ...this.mapBodyFilters(filters),
            pageNum,
            pageSize,
            sort,
            restrictToOpen,
        };
        return this.serviceHandler.post('invoice', body).pipe(filter((resp) => !!resp), catchError((error) => throwError(error)));
    }
    getStatusSummary(filters, restrictToOpen = false, omitFilterNameStatus = true) {
        return this.serviceHandler
            .post('status', {
            restrictToOpen,
            needISR: false,
            ...this.mapBodyFilters(filters, omitFilterNameStatus),
        })
            .pipe(filter((resp) => !!resp), catchError((error) => throwError(error)));
    }
    getPaymentActivityStatusSummary(filters, omitFilterNameStatus = true) {
        return this.paymentServiceHandler
            .post('paymentActivityStatus', this.mapBodyFilters(filters, omitFilterNameStatus))
            .pipe(filter((resp) => !!resp), catchError((error) => throwError(error)));
    }
    /**
     * Generates a full ACH form for a given audit ID.
     * Makes a POST request to generate an ACH form with encryption.
     *
     * @param auditID - The unique identifier for the audit record
     * @returns Observable<AchFullFormResponse> - Returns an observable containing:
     *  - encryptionKey: The key used for encrypting sensitive ACH data
     *  - achAuditID: The audit ID associated with the generated ACH form
     * @throws Will throw an error if the API call fails or returns invalid data
     */
    getGeneratedACHForm(auditID) {
        return this.bankServiceHandler
            .post('generateACHForm/full', {
            auditID,
        })
            .pipe(filter((resp) => !!resp), catchError((error) => throwError(error)));
    }
    getBankDetails(customers, primaryOnly = true) {
        return this.bankServiceHandler
            .post('bankDetails', {
            customers: customers.length > 0 ? customers : null,
            primaryOnly,
        })
            .pipe(filter((resp) => !!resp.customerBankDetails), catchError((error) => throwError(error)));
    }
    postBankAccounts(newCustomerBankAccount) {
        return this.bankServiceHandler
            .post('paymentPreference', newCustomerBankAccount)
            .pipe(filter((resp) => !!resp), catchError((error) => throwError(error)));
    }
    postADBankAccounts(newADBankAccount) {
        return this.bankServiceHandler
            .post('enrollADBankAccount', newADBankAccount)
            .pipe(filter((resp) => !!resp), catchError((error) => throwError(error)));
    }
    submitPayment(scheduledPaymentDate, submitCustomerPayments) {
        return this.paymentServiceHandler
            .post('submitPayment', {
            scheduledPaymentDate,
            customerPayments: submitCustomerPayments,
        })
            .pipe(filter((resp) => !!resp.customerPayments), catchError((error) => throwError(error)));
    }
    getPaymentDetails(paymentActivityID) {
        return this.paymentServiceHandler
            .post('paymentDetails', {
            paymentActivityID,
        })
            .pipe(filter((resp) => !!resp.paymentDetails), catchError((error) => throwError(error)));
    }
    cancelPayment(paymentActivityID) {
        return this.paymentServiceHandler
            .post('cancelPayment', {
            paymentActivityID,
        })
            .pipe(catchError((error) => throwError(error)));
    }
    deleteBankAccount(bankId) {
        return this.bankServiceHandler
            .delete(`bankAccount?bankID=${bankId}`)
            .pipe(catchError((error) => throwError(error)));
    }
    getUniqueBankDetails() {
        return this.bankServiceHandler
            .get(`uniqueBankDetails`)
            .pipe(catchError((error) => throwError(error)));
    }
    getEligibleADCustomers() {
        return this.bankServiceHandler
            .get(`eligibleADCustomers`)
            .pipe(catchError((error) => throwError(error)));
    }
    getMetricSummary(customerNumbers) {
        return this.paymentMetricHandler
            .post(`metricSummary`, { customerNumbers })
            .pipe(catchError((error) => throwError(error)));
    }
    getMetricDetails(customerNumbers) {
        return this.paymentMetricHandler
            .post(`metricDetails`, { customerNumbers })
            .pipe(catchError((error) => throwError(error)));
    }
    fetchPaymentActivity({ pageNum, pageSize, sort, filters, headerOnly, }) {
        return this.paymentServiceHandler
            .post('paymentActivity', {
            ...this.mapBodyFilters(filters),
            pageNum,
            pageSize,
            sort,
            headerOnly,
        })
            .pipe(catchError((error) => throwError(error)));
    }
    mapBodyFilters(filters, omitStatusFilter = false) {
        return filters.reduce((prev, invoiceFilter) => {
            if (invoiceFilter.filterName === InvoiceFilterIds.Status &&
                omitStatusFilter) {
                return prev;
            }
            return {
                ...prev,
                [invoiceFilter.filterName]: invoiceFilter.filterName === InvoiceFilterIds.NeedISR
                    ? invoiceFilter.value.length > 0
                    : invoiceFilter.value,
            };
        }, {});
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: PaymentApiService, deps: [{ token: PaymentDataHttpClientService }, { token: BankHttpClientService }, { token: PaymentHttpClientService }, { token: PaymentMetricHttpClientService }, { token: ArStatementsHttpClientService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: PaymentApiService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: PaymentApiService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: PaymentDataHttpClientService }, { type: BankHttpClientService }, { type: PaymentHttpClientService }, { type: PaymentMetricHttpClientService }, { type: ArStatementsHttpClientService }]; } });

class AccountSummaryEffects {
    constructor(actions$, paymentApiService) {
        this.actions$ = actions$;
        this.paymentApiService = paymentApiService;
        this.getAccountSummary$ = createEffect(() => this.actions$.pipe(ofType(ACCOUNT_SUMMARY_ACTIONS.getAccountSummary), switchMap((payload) => this.paymentApiService.getAccountSummary(payload.customers).pipe(map((accountSummary) => ACCOUNT_SUMMARY_ACTIONS.getAccountSummarySuccess({
            accountSummary,
        })), catchError((error) => of(ACCOUNT_SUMMARY_ACTIONS.getAccountSummaryFail({ error })))))));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AccountSummaryEffects, deps: [{ token: i1$2.Actions }, { token: PaymentApiService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AccountSummaryEffects, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AccountSummaryEffects, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1$2.Actions }, { type: PaymentApiService }]; } });

class ArStatementEffects {
    constructor(actions$, store, paymentApiService) {
        this.actions$ = actions$;
        this.store = store;
        this.paymentApiService = paymentApiService;
        this.getArStatements$ = createEffect(() => this.actions$.pipe(ofType(AR_STATEMENT_ACTIONS.getArStatements), withLatestFrom(this.store.select(arStatementSelectors.selectArStatements)), switchMap(([payload, state]) => this.paymentApiService
            .getArStatementsUsingSortAndFilter(payload && payload.pageNum ? payload.pageNum : 1, state && state.sort ? state.sort : null, state && state.filters ? state.filters : null, payload ? payload.pageSize ?? undefined : undefined)
            .pipe(map((response) => AR_STATEMENT_ACTIONS.getArStatementsSuccess({
            pageNum: payload.pageNum,
            arStatements: response.arStatements,
            nextPage: response.nextPage,
        })), catchError((error) => of(AR_STATEMENT_ACTIONS.getArStatementsFail({ error })))))));
        this.getArStatementsOnFilterOrSortChange$ = createEffect(() => this.actions$.pipe(ofType(AR_STATEMENT_ACTIONS.setArStatementsFilters, AR_STATEMENT_ACTIONS.setArStatementsSortOrder), withLatestFrom(this.store.select(arStatementSelectors.selectArStatements)), switchMap(([payload, state]) => this.paymentApiService
            .getArStatementsUsingSortAndFilter(1, state && state.sort ? state.sort : null, state && state.filters ? state.filters : null, payload ? payload.pageSize ?? undefined : undefined)
            .pipe(map((response) => AR_STATEMENT_ACTIONS.getArStatementsSuccess({
            pageNum: 1,
            arStatements: response.arStatements,
            nextPage: response.nextPage,
        })), catchError((error) => of(AR_STATEMENT_ACTIONS.getArStatementsFail({ error })))))));
        this.selectMaxStatements$ = createEffect(() => this.actions$.pipe(ofType(AR_STATEMENT_ACTIONS.selectMaxStatements), withLatestFrom(this.store.select(arStatementSelectors.selectArStatements)), switchMap(([payload, state]) => {
            const maxSelection = payload.maxSelection || MAX_STATEMENT_SELECTION;
            const newSelection = state.ids
                .filter((statementId) => !state.selectedIds.includes(statementId))
                .slice(0, maxSelection);
            return of(AR_STATEMENT_ACTIONS.selectStatements({
                statementsSelected: newSelection,
            }));
        })));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: ArStatementEffects, deps: [{ token: i1$2.Actions }, { token: i1.Store }, { token: PaymentApiService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: ArStatementEffects, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: ArStatementEffects, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1$2.Actions }, { type: i1.Store }, { type: PaymentApiService }]; } });

class BankEffects {
    constructor(actions$, store, paymentApiService) {
        this.actions$ = actions$;
        this.store = store;
        this.paymentApiService = paymentApiService;
        this.getBankDetails$ = createEffect(() => this.actions$.pipe(ofType(BANK_ACTIONS.getBankDetails), switchMap((payload) => this.paymentApiService
            .getBankDetails(payload.customers, payload.primaryOnly)
            .pipe(map((customerBankDetails) => BANK_ACTIONS.getBankDetailsSuccess({
            customerBankDetails: customerBankDetails.customerBankDetails,
        })), catchError((error) => of(BANK_ACTIONS.getBankDetailsFail({ error })))))));
        this.getUniqueBankDetails$ = createEffect(() => this.actions$.pipe(ofType(BANK_ACTIONS.getUniqueBankDetails), switchMap(() => this.paymentApiService.getUniqueBankDetails().pipe(map(({ uniqueBankDetails }) => BANK_ACTIONS.getUniqueBankDetailsSuccess({
            uniqueBankDetails,
        })), catchError((error) => of(BANK_ACTIONS.getBankDetailsFail({ error })))))));
        this.getEligibleADCustomers$ = createEffect(() => this.actions$.pipe(ofType(BANK_ACTIONS.getEligibleADCustomers), switchMap(() => this.paymentApiService.getEligibleADCustomers().pipe(map(({ eligibleCustomers }) => BANK_ACTIONS.getEligibleADCustomersSuccess({
            eligibleCustomers,
        })), catchError((error) => of(BANK_ACTIONS.getBankDetailsFail({ error })))))));
        this.postBankAccounts$ = createEffect(() => this.actions$.pipe(ofType(BANK_ACTIONS.postBankAccounts), switchMap((payload) => this.paymentApiService
            .postBankAccounts(payload.newCustomerBankAccount)
            .pipe(map((response) => BANK_ACTIONS.postBankAccountsSuccess({ response })), catchError((error) => of(BANK_ACTIONS.postBankAccountsFail({ error })))))));
        this.postADBankAccounts$ = createEffect(() => this.actions$.pipe(ofType(BANK_ACTIONS.postADBankAccounts), switchMap((payload) => this.paymentApiService
            .postADBankAccounts(payload.newADBankAccount)
            .pipe(map((response) => BANK_ACTIONS.postADBankAccountsSuccess(response)), catchError((error) => of(BANK_ACTIONS.postADBankAccountsFail({ error })))))));
        this.deleteBankAccount$ = createEffect(() => this.actions$.pipe(ofType(BANK_ACTIONS.deleteBankAccount), switchMap((payload) => this.paymentApiService.deleteBankAccount(payload.bankID).pipe(map((response) => BANK_ACTIONS.deleteBankAccountSuccess({ ...response })), catchError((error) => of(BANK_ACTIONS.deleteBankAccountFail({ error })))))));
        this.addCustomerBankAccount$ = createEffect(() => this.actions$.pipe(ofType(BANK_ACTIONS.addCustomerBankAccount), withLatestFrom(this.store.select(bankSelectors.selectBank)), switchMap(([payload, bankState]) => this.paymentApiService
            .postBankAccounts(payload.newCustomerBankAccount)
            .pipe(map((response) => {
            const { bankID } = response.customerBankIDs.find((bankMap) => bankMap.customerNumber ===
                payload.newCustomerBankAccount.customerNumbers[0]);
            const bankAccount = {
                ...payload.newCustomerBankAccount,
                last4AccountNumber: payload.newCustomerBankAccount.accountNumber.slice(-4),
                isPrimary: true,
                bankID,
                customerNumber: payload.newCustomerBankAccount.customerNumbers[0],
            };
            return BANK_ACTIONS.addCustomerBankAccountSuccess({
                customerBankDetails: this.addNewBankAccount(bankState.customerBankDetails, bankAccount),
                newBankAccount: bankAccount,
            });
        }), catchError((error) => of(BANK_ACTIONS.addCustomerBankAccountFail({ error })))))));
    }
    addNewBankAccount(customersBanksDetails, newBankAccount) {
        const newBankDetails = [...customersBanksDetails];
        let customerIndex = newBankDetails.findIndex((customerBankDetails) => customerBankDetails.customerNumber === newBankAccount.customerNumber);
        if (customerIndex === -1) {
            newBankDetails.push({
                customerNumber: newBankAccount.customerNumber,
                bankDetails: [],
            });
            customerIndex = newBankDetails.length - 1;
        }
        newBankDetails[customerIndex] = {
            ...newBankDetails[customerIndex],
            bankDetails: [
                ...newBankDetails[customerIndex].bankDetails.map((bankDetail) => ({
                    ...bankDetail,
                    isPrimary: false,
                })),
                { ...newBankAccount, isPrimary: true },
            ],
        };
        return newBankDetails;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: BankEffects, deps: [{ token: i1$2.Actions }, { token: i1.Store }, { token: PaymentApiService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: BankEffects, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: BankEffects, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1$2.Actions }, { type: i1.Store }, { type: PaymentApiService }]; } });

class AchFormEffects {
    constructor(actions$, store, paymentApiService) {
        this.actions$ = actions$;
        this.store = store;
        this.paymentApiService = paymentApiService;
        /**
         * Story: R4PT-5567 - Get Generated ACH Form
         *
         * This effect is used to get the generated ACH form from the API.
         * It dispatches an action to the store to get the generated ACH form.
         */
        this.getGeneratedACHForm$ = createEffect(() => this.actions$.pipe(ofType(ACH_FORM_ACTIONS.getGeneratedACHForm), switchMap((payload) => this.paymentApiService.getGeneratedACHForm(payload.auditID).pipe(map((response) => ACH_FORM_ACTIONS.getGeneratedACHFormSuccess({
            encryptionKey: response.encryptionKey,
            achAuditID: response.achAuditID,
        })), catchError((error) => of(ACH_FORM_ACTIONS.getGeneratedACHFormFail({ error })))))));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AchFormEffects, deps: [{ token: i1$2.Actions }, { token: i1.Store }, { token: PaymentApiService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AchFormEffects, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AchFormEffects, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1$2.Actions }, { type: i1.Store }, { type: PaymentApiService }]; } });

class InvoiceEffects {
    constructor(actions$, store, paymentApiService) {
        this.actions$ = actions$;
        this.store = store;
        this.paymentApiService = paymentApiService;
        this.getInvoices$ = createEffect(() => this.actions$.pipe(ofType(INVOICE_ACTIONS.getInvoices), withLatestFrom(this.store.select(invoicesSelectors.selectInvoices)), switchMap(([payload, state]) => this.paymentApiService
            .getInvoicesUsingSortAndFilter(payload && payload.pageNum ? payload.pageNum : 1, state && state.sort ? state.sort : null, state && state.filters ? state.filters : null, payload ? payload.pageSize ?? undefined : undefined, payload.restrictToOpen)
            .pipe(map((response) => INVOICE_ACTIONS.getInvoicesSuccess({
            pageNum: payload.pageNum,
            invoices: response.invoices,
            nextPage: response.nextPage,
            warning: response.warning,
        })), catchError((error) => of(INVOICE_ACTIONS.getInvoicesFail({ error })))))));
        this.getInvoicesOnFilterOrSortChange$ = createEffect(() => this.actions$.pipe(ofType(INVOICE_ACTIONS.setInvoiceFilters, INVOICE_ACTIONS.setInvoiceSortOrder), withLatestFrom(this.store.select(invoicesSelectors.selectInvoices)), switchMap(([payload, state]) => this.paymentApiService
            .getInvoicesUsingSortAndFilter(1, state && state.sort ? state.sort : null, state && state.filters ? state.filters : null, payload ? payload.pageSize ?? undefined : undefined, payload.restrictToOpen)
            .pipe(map((response) => INVOICE_ACTIONS.getInvoicesSuccess({
            pageNum: 1,
            invoices: response.invoices,
            nextPage: response.nextPage,
            warning: response.warning,
        })), catchError((error) => of(INVOICE_ACTIONS.getInvoicesFail({ error })))))));
        this.selectMaxInvoices$ = createEffect(() => this.actions$.pipe(ofType(INVOICE_ACTIONS.selectMaxInvoices), withLatestFrom(this.store.select(invoicesSelectors.selectInvoices)), switchMap(([payload, state]) => {
            const maxSelection = payload.maxSelection || MAX_INVOICE_SELECTION;
            const newSelection = state.ids
                .filter((transactionId) => !state.selectedIds.includes(transactionId))
                .slice(0, maxSelection);
            return of(INVOICE_ACTIONS.selectInvoices(newSelection));
        })));
        this.updateInvoices$ = createEffect(() => this.actions$.pipe(ofType(PAYMENT_ACTIONS.submitPaymentSuccess), withLatestFrom(this.store.select(invoicesSelectors.selectInvoices)), map(([payload, _state]) => {
            const paymentInvoices = payload.customerPayments.reduce((prev, next) => [...prev, ...next.paymentInvoices], []);
            return INVOICE_ACTIONS.updateInvoiceAfterPay({
                paymentInvoices,
                postingDate: payload.postingDate,
            });
        })));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: InvoiceEffects, deps: [{ token: i1$2.Actions }, { token: i1.Store }, { token: PaymentApiService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: InvoiceEffects, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: InvoiceEffects, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1$2.Actions }, { type: i1.Store }, { type: PaymentApiService }]; } });

class OpenInvoicesEffects {
    constructor(actions$, paymentApiService) {
        this.actions$ = actions$;
        this.paymentApiService = paymentApiService;
        this.getOpenInvoices$ = createEffect(() => this.actions$.pipe(ofType(OPEN_INVOICES_ACTIONS.getOpenInvoices), switchMap((payload) => this.paymentApiService
            .getStatusSummary(payload.filters, payload.restrictToOpen, false)
            .pipe(map((status) => Object.values(status).reduce((acc, prev) => acc + prev, 0)), map((totalOpenInvoices) => OPEN_INVOICES_ACTIONS.getOpenInvoicesSuccess({
            totalOpenInvoices,
        })), catchError((error) => of(OPEN_INVOICES_ACTIONS.getOpenInvoicesFail({ error })))))));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: OpenInvoicesEffects, deps: [{ token: i1$2.Actions }, { token: PaymentApiService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: OpenInvoicesEffects, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: OpenInvoicesEffects, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1$2.Actions }, { type: PaymentApiService }]; } });

class PaymentMetricEffects {
    constructor(actions$, paymentApiService) {
        this.actions$ = actions$;
        this.paymentApiService = paymentApiService;
        this.getMetricSummary$ = createEffect(() => this.actions$.pipe(ofType(PAYMENT_METRIC_ACTIONS.getMetricSummary), switchMap((payload) => this.paymentApiService.getMetricSummary(payload.customerNumbers).pipe(map((metricSummary) => PAYMENT_METRIC_ACTIONS.getMetricSummarySuccess(metricSummary)), catchError((error) => of(PAYMENT_METRIC_ACTIONS.getMetricSummaryFail({ error })))))));
        this.getMetricDetails$ = createEffect(() => this.actions$.pipe(ofType(PAYMENT_METRIC_ACTIONS.getMetricDetails), switchMap((payload) => this.paymentApiService.getMetricDetails(payload.customerNumbers).pipe(map((metricSummary) => PAYMENT_METRIC_ACTIONS.getMetricDetailsSuccess(metricSummary)), catchError((error) => of(PAYMENT_METRIC_ACTIONS.getMetricDetailsFail({ error })))))));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: PaymentMetricEffects, deps: [{ token: i1$2.Actions }, { token: PaymentApiService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: PaymentMetricEffects, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: PaymentMetricEffects, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1$2.Actions }, { type: PaymentApiService }]; } });

class PaymentEffects {
    constructor(actions$, store, paymentApiService) {
        this.actions$ = actions$;
        this.store = store;
        this.paymentApiService = paymentApiService;
        this.submitPayment$ = createEffect(() => this.actions$.pipe(ofType(PAYMENT_ACTIONS.submitPayment), withLatestFrom(this.store.select(invoicesSelectors.selectInvoices), this.store.select(bankSelectors.selectBank)), switchMap(([payload, invoiceState, bankDetailsState]) => {
            const customerPayments = this.getCustomerPayments(invoiceState.selectedTransactions, invoiceState.translatedIds, bankDetailsState.selectedBankAccounts);
            return this.paymentApiService
                .submitPayment(payload.scheduledPaymentDate, customerPayments)
                .pipe(map((submitPaymentResponse) => PAYMENT_ACTIONS.submitPaymentSuccess({
                ...submitPaymentResponse,
                ...payload,
            })), catchError((error) => of(PAYMENT_ACTIONS.submitPaymentFail({ error }))));
        })));
        this.getPaymentDetails$ = createEffect(() => this.actions$.pipe(ofType(PAYMENT_ACTIONS.getPaymentDetails), switchMap((payload) => this.paymentApiService
            .getPaymentDetails(payload.paymentActivityID)
            .pipe(map((paymentDetails) => PAYMENT_ACTIONS.getPaymentDetailsSuccess({ paymentDetails })), catchError((error) => of(PAYMENT_ACTIONS.getPaymentDetailsFail({ error })))))));
        this.cancelPayment$ = createEffect(() => this.actions$.pipe(ofType(PAYMENT_ACTIONS.cancelPayment), switchMap((payload) => this.paymentApiService.cancelPayment(payload.paymentActivityID).pipe(map(() => PAYMENT_ACTIONS.cancelPaymentSuccess()), catchError((error) => of(PAYMENT_ACTIONS.cancelPaymentFail({ error })))))));
        this.setFilterOrSort$ = createEffect(() => this.actions$.pipe(ofType(PAYMENT_ACTIONS.setPaymentActivityFilters, PAYMENT_ACTIONS.setPaymentActivitySortOrder), map((payload) => PAYMENT_ACTIONS.getPaymentActivity({
            pageNum: 1,
            pageSize: payload.pageSize,
        })), catchError((error) => of(PAYMENT_ACTIONS.getPaymentActivityFail({ error })))));
        this.getPaymentActivity$ = createEffect(() => this.actions$.pipe(ofType(PAYMENT_ACTIONS.getPaymentActivity), withLatestFrom(this.store.select(paymentSelectors.selectPaymentActivity)), switchMap(([payload, paymentActivitiesState]) => this.paymentApiService
            .fetchPaymentActivity({
            ...payload,
            filters: paymentActivitiesState.filters,
            sort: paymentActivitiesState.sort,
        })
            .pipe(map((paymentActivitiesResponse) => PAYMENT_ACTIONS.getPaymentActivitySuccess({
            ...paymentActivitiesResponse,
            pageNum: payload.pageNum,
        })), catchError((error) => of(PAYMENT_ACTIONS.getPaymentActivityFail({ error })))))));
    }
    getCustomerPayments(selectedTransactions, translatedIds, bankDetails) {
        const customerPayments = [];
        const invoiceIds = Object.keys(selectedTransactions);
        invoiceIds.map((id) => {
            let customerIndex = customerPayments.findIndex((customer) => customer.customerNumber === selectedTransactions[id].customerNumber);
            if (customerIndex === -1) {
                const customerBankDetails = bankDetails.find((customerBD) => customerBD.customerNumber ===
                    selectedTransactions[id].customerNumber);
                customerPayments?.push({
                    customerNumber: selectedTransactions[id].customerNumber,
                    bankID: customerBankDetails.bankID,
                    paymentInvoices: [],
                });
                customerIndex = customerPayments.length - 1;
            }
            let invoiceID = id;
            if (translatedIds[id] != null) {
                invoiceID = translatedIds[id];
            }
            customerPayments[customerIndex].paymentInvoices.push({
                invoiceID,
                remitAmount: selectedTransactions[id].paymentAmount,
                shortPayCode: selectedTransactions[id].shortPayCode ?? null,
            });
        });
        return customerPayments;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: PaymentEffects, deps: [{ token: i1$2.Actions }, { token: i1.Store }, { token: PaymentApiService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: PaymentEffects, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: PaymentEffects, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1$2.Actions }, { type: i1.Store }, { type: PaymentApiService }]; } });

const getSortsForRecentCredits = () => [
    {
        fieldName: SortFields.issueDate,
        sortValue: SortDirection.DESC,
    },
    {
        fieldName: SortFields.transaction,
        sortValue: SortDirection.DESC,
    },
];
const getFiltersForRecentCredits = (transactionGroupFilters, customers) => {
    const thirtyDays = 1000 * 60 * 60 * 24 * 30;
    const now = new Date();
    const todaysDate = formatDate(now);
    const thirtyDaysAgo = formatDate(new Date(now.valueOf() - thirtyDays));
    return [
        {
            filterName: InvoiceFilterIds.FromIssueDate,
            value: thirtyDaysAgo,
        },
        {
            filterName: InvoiceFilterIds.ToIssueDate,
            value: todaysDate,
        },
        {
            filterName: InvoiceFilterIds.TransactionGroup,
            value: transactionGroupFilters,
        },
        {
            filterName: InvoiceFilterIds.Customers,
            value: customers,
        },
    ];
};
const formatDate = (date) => +date.toJSON().replace(/-/g, '').substring(0, 8);

class RecentCreditEffects {
    constructor(actions$, paymentApiService) {
        this.actions$ = actions$;
        this.paymentApiService = paymentApiService;
        this.getRecentCredits$ = createEffect(() => this.actions$.pipe(ofType(RECENT_CREDIT_ACTIONS.getRecentCredits), switchMap((payload) => this.paymentApiService
            .getInvoicesUsingSortAndFilter(1, getSortsForRecentCredits(), getFiltersForRecentCredits([TransactionGroupFilter.CREDIT], payload.customers), 10)
            .pipe(map((response) => RECENT_CREDIT_ACTIONS.getRecentCreditsSuccess({
            invoices: response.invoices,
        })), catchError((error) => of(RECENT_CREDIT_ACTIONS.getRecentCreditsFail({ error })))))));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: RecentCreditEffects, deps: [{ token: i1$2.Actions }, { token: PaymentApiService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: RecentCreditEffects, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: RecentCreditEffects, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1$2.Actions }, { type: PaymentApiService }]; } });

class ShareEmailApiService {
    constructor(http, config) {
        this.http = http;
        this.config = config;
    }
    sharePaymentConfirmationViaEmail(data) {
        return this.http
            .post(this.config.fileShareApiUrl + '/sharePaymentConfirmation', data)
            ?.pipe(filter((resp) => !!resp), catchError((error) => throwError(error)));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: ShareEmailApiService, deps: [{ token: i1$1.HttpClient }, { token: NGRX_PAYMENT_LIB_CONFIG }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: ShareEmailApiService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: ShareEmailApiService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1$1.HttpClient }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [NGRX_PAYMENT_LIB_CONFIG]
                }] }]; } });

class ShareEmailEffects {
    constructor(actions$, shareEmailApiService) {
        this.actions$ = actions$;
        this.shareEmailApiService = shareEmailApiService;
        this.sharePaymentConfirmationViaEmail$ = createEffect(() => this.actions$.pipe(ofType(SHARE_EMAIL_ACTIONS.sharePaymentConfirmationViaEmail), switchMap((params) => this.shareEmailApiService
            .sharePaymentConfirmationViaEmail(params.data)
            .pipe(switchMap((response) => [
            SHARE_EMAIL_ACTIONS.sharePaymentConfirmationViaEmailSuccess({
                response,
            }),
        ]), catchError((err) => {
            const error = { status: err.status, message: err.message };
            return of(SHARE_EMAIL_ACTIONS.sharePaymentConfirmationViaEmailFail({
                error,
            }));
        })))));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: ShareEmailEffects, deps: [{ token: i1$2.Actions }, { token: ShareEmailApiService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: ShareEmailEffects, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: ShareEmailEffects, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1$2.Actions }, { type: ShareEmailApiService }]; } });

class StatusSummaryEffects {
    constructor(actions$, store, paymentApiService) {
        this.actions$ = actions$;
        this.store = store;
        this.paymentApiService = paymentApiService;
        this.getStatusSummary$ = createEffect(() => this.actions$.pipe(ofType(STATUS_SUMMARY_ACTIONS.getStatusSummary), switchMap((payload) => this.paymentApiService
            .getStatusSummary(payload.filters, payload.restrictToOpen)
            .pipe(map((statusSummary) => STATUS_SUMMARY_ACTIONS.getStatusSummarySuccess({
            statusSummary,
        })), catchError((error) => of(STATUS_SUMMARY_ACTIONS.getStatusSummaryFail({ error })))))));
        this.reloadStatusSummary$ = createEffect(() => this.actions$.pipe(ofType(STATUS_SUMMARY_ACTIONS.reloadStatusSummary), withLatestFrom(this.store.select(invoicesSelectors.selectInvoices)), map(([payload, { filters }]) => STATUS_SUMMARY_ACTIONS.getStatusSummary({
            filters,
            restrictToOpen: payload.restrictToOpen,
        }))));
        this.getPaymentActivityStatusSummary$ = createEffect(() => this.actions$.pipe(ofType(STATUS_SUMMARY_ACTIONS.getPaymentActivityStatusSummary), switchMap((payload) => this.paymentApiService
            .getPaymentActivityStatusSummary(payload.filters)
            .pipe(map((statusSummary) => STATUS_SUMMARY_ACTIONS.getPaymentActivityStatusSummarySuccess({
            statusSummary,
        })), catchError((error) => of(STATUS_SUMMARY_ACTIONS.getPaymentActivityStatusSummaryFail({
            error,
        })))))));
        this.reloadPaymentActivityStatusSummary$ = createEffect(() => this.actions$.pipe(ofType(STATUS_SUMMARY_ACTIONS.reloadPaymentActivityStatusSummary), withLatestFrom(this.store.select(paymentSelectors.selectPaymentActivity)), map(([, { filters }]) => STATUS_SUMMARY_ACTIONS.getPaymentActivityStatusSummary({
            filters,
        }))));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: StatusSummaryEffects, deps: [{ token: i1$2.Actions }, { token: i1.Store }, { token: PaymentApiService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: StatusSummaryEffects, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: StatusSummaryEffects, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1$2.Actions }, { type: i1.Store }, { type: PaymentApiService }]; } });

const initialBankState = {
    loading: false,
    error: null,
    customerBankDetails: [],
    uniqueBankAccounts: [],
    selectedBankAccounts: [],
    eligibleADCustomers: [],
    enrolledADCustomersResponse: [],
    warning: null,
};
const reducer$c = createReducer(initialBankState, on(BANK_ACTIONS.getBankDetails, (state, _action) => ({
    ...initialBankState,
    eligibleADCustomers: state.eligibleADCustomers,
    loading: true,
})), on(BANK_ACTIONS.getBankDetailsSuccess, (state, action) => ({
    ...state,
    customerBankDetails: action.customerBankDetails,
    loading: false,
    error: null,
})), on(BANK_ACTIONS.getBankDetailsFail, (_state, action) => ({
    ...initialBankState,
    loading: false,
    error: action.error,
})), on(BANK_ACTIONS.addCustomerBankAccount, (state, _action) => ({
    ...state,
    loading: true,
})), on(BANK_ACTIONS.setBankAccountsForPaying, (state, action) => ({
    ...state,
    selectedBankAccounts: setNewBankAccountSelected(action.bankAccounts, state.selectedBankAccounts),
})), on(BANK_ACTIONS.addCustomerBankAccountSuccess, (state, action) => ({
    ...state,
    selectedBankAccounts: setNewBankAccountSelected([action.newBankAccount], state.selectedBankAccounts),
    customerBankDetails: action.customerBankDetails,
    loading: false,
    error: null,
})), on(BANK_ACTIONS.addCustomerBankAccountFail, (state, action) => ({
    ...state,
    loading: false,
    error: action.error,
})), on(BANK_ACTIONS.postBankAccounts, (state, _) => ({
    ...state,
    loading: true,
})), on(BANK_ACTIONS.postBankAccountsSuccess, (state, action) => ({
    ...state,
    loading: false,
    warning: action.response?.warning || null,
})), on(BANK_ACTIONS.postBankAccountsFail, (state, action) => ({
    ...state,
    loading: false,
    error: action.error,
})), on(BANK_ACTIONS.deleteBankAccount, (state, _) => ({
    ...state,
    loading: true,
})), on(BANK_ACTIONS.deleteBankAccountSuccess, (state, action) => ({
    ...state,
    loading: false,
})), on(BANK_ACTIONS.deleteBankAccountFail, (state, action) => ({
    ...state,
    loading: false,
    error: action.error,
})), on(BANK_ACTIONS.clearError, (state, _action) => ({
    ...state,
    loading: false,
    error: null,
})), on(BANK_ACTIONS.getUniqueBankDetails, (state, _action) => ({
    ...state,
    loading: true,
})), on(BANK_ACTIONS.getUniqueBankDetailsSuccess, (state, action) => ({
    ...state,
    uniqueBankAccounts: action.uniqueBankDetails,
    loading: false,
    error: null,
})), on(BANK_ACTIONS.getUniqueBankDetailsFail, (state, action) => ({
    ...state,
    loading: false,
    error: action.error,
})), on(BANK_ACTIONS.getEligibleADCustomers, (state, _action) => ({
    ...state,
    loading: true,
})), on(BANK_ACTIONS.getEligibleADCustomersSuccess, (state, action) => ({
    ...state,
    eligibleADCustomers: action.eligibleCustomers,
    loading: false,
    error: null,
})), on(BANK_ACTIONS.getEligibleADCustomersFail, (state, action) => ({
    ...state,
    loading: false,
    error: action.error,
})), on(BANK_ACTIONS.postADBankAccounts, (state, _action) => ({
    ...state,
    loading: true,
})), on(BANK_ACTIONS.postADBankAccountsSuccess, (state, action) => ({
    ...state,
    loading: false,
    error: null,
    enrolledADCustomersResponse: action.customerResponses,
})), on(BANK_ACTIONS.postADBankAccountsFail, (state, action) => ({
    ...state,
    loading: false,
    error: action.error,
})), on(BANK_ACTIONS.clearEligibleADCustomers, (state) => ({
    ...state,
    eligibleADCustomers: [],
    enrolledADCustomersResponse: [],
})));
// eslint-disable-next-line prefer-arrow/prefer-arrow-functions
function bankReducer(state, action) {
    return reducer$c(state, action);
}
const setNewBankAccountSelected = (newSelectedBankAccounts, selectedBankAccounts = []) => newSelectedBankAccounts.reduce((prev, current) => {
    const newArray = [...prev];
    const selectedIndex = newArray.findIndex((selectedBankAccount) => current.customerNumber === selectedBankAccount.customerNumber);
    if (selectedIndex !== -1) {
        newArray[selectedIndex] = current;
        return newArray;
    }
    return [...newArray, current];
}, selectedBankAccounts);

const keys$1 = ['customerBankDetails'];
const PAYMENT_BANK_DETAIL_CONFIG_TOKEN = new InjectionToken(PAYMENT_BANK_DETAILS);
const sessionStorageSyncReducer$1 = (reducer) => (state, action) => {
    const nextState = reducer(state, action);
    switch (action.type) {
        case BANK_ACTIONS.addCustomerBankAccountSuccess.type:
        case BANK_ACTIONS.getBankDetailsSuccess.type:
            keys$1.forEach((key) => {
                sessionStorage.setItem(key, JSON.stringify(nextState[key]));
            });
            break;
    }
    return nextState;
};
const bankDetailStorageMetaReducers = [
    sessionStorageSyncReducer$1,
];
const paymentBankDetailConfigFactory = () => {
    const initialState = { ...initialBankState };
    keys$1.forEach((key) => {
        if (sessionStorage.getItem(key)) {
            const data = sessionStorage.getItem(key);
            const raw = JSON.parse(data);
            initialState[key] = raw;
        }
    });
    return {
        initialState,
        metaReducers: bankDetailStorageMetaReducers,
    };
};

const invoiceAdapter = createEntityAdapter({
    // eslint-disable-next-line no-underscore-dangle
    selectId: (transaction) => transaction._id,
});
const emptyIds = [];
const emptyTransactions = {};
const initialInvoices = invoiceAdapter.getInitialState({
    loading: false,
    error: null,
    nextPage: false,
    pageNum: 0,
    filters: [],
    initialSelectedTransactions: emptyTransactions,
    selectedIds: emptyIds,
    selectedTransactions: emptyTransactions,
    translatedIds: {},
    sort: [],
    postingDate: 0,
    warning: null,
});
const reducer$b = createReducer(initialInvoices, on(INVOICE_ACTIONS.getInvoices, (state, action) => {
    if (action.pageNum === 1) {
        let invoiceState = {
            ...initialInvoices,
            filters: state.filters,
            sort: state.sort,
            loading: true,
        };
        if (action.keepSelectedInvoices || false) {
            invoiceState = {
                ...invoiceState,
                initialSelectedTransactions: state.initialSelectedTransactions,
                selectedIds: state.selectedIds,
                selectedTransactions: state.selectedTransactions,
            };
        }
        return invoiceState;
    }
    else {
        return {
            ...state,
            loading: true,
        };
    }
}), on(INVOICE_ACTIONS.getInvoicesSuccess, (state, action) => {
    if (action.pageNum === 1) {
        return invoiceAdapter.setAll(action.invoices ?? [], {
            ...state,
            loading: false,
            error: null,
            nextPage: action.nextPage,
            pageNum: action.pageNum,
            warning: action.warning,
        });
    }
    else {
        return invoiceAdapter.addMany(action.invoices, {
            ...state,
            loading: false,
            error: null,
            nextPage: action.nextPage,
            pageNum: action.pageNum,
            warning: action.warning,
        });
    }
}), on(INVOICE_ACTIONS.getInvoicesFail, (state, action) => ({
    ...state,
    loading: false,
    error: action.error,
})), on(INVOICE_ACTIONS.clearSelectedInvoices, (state, action) => ({
    ...state,
    selectedIds: [],
    selectedTransactions: emptyTransactions,
    initialSelectedTransactions: emptyTransactions,
})), on(INVOICE_ACTIONS.selectInvoices, (state, action) => {
    let newSelectedIds = [...state.selectedIds];
    let newSelectedTransactions = { ...state.selectedTransactions };
    let newTranslatedIds = { ...state.translatedIds };
    action.ids.map((id) => {
        const wasSelected = newSelectedIds.includes(id);
        if (!wasSelected) {
            newSelectedIds = [...newSelectedIds, id];
            const newTransaction = Object.keys(state.entities).includes(id)
                ? { ...state.entities[id] }
                : { ...state.initialSelectedTransactions[id] };
            if (newTransaction.netAmount) {
                newTransaction.paymentAmount = newTransaction.netAmount;
            }
            const newKeyedTransaction = {};
            newKeyedTransaction[id] = { ...newTransaction };
            newSelectedTransactions = {
                ...newSelectedTransactions,
                ...newKeyedTransaction,
            };
            newTranslatedIds = { ...newTranslatedIds, ...action.translatedIds };
        }
        else {
            newSelectedIds.splice(newSelectedIds.indexOf(id), 1);
            delete newSelectedTransactions[id];
        }
    });
    return {
        ...state,
        translatedIds: newTranslatedIds,
        selectedIds: newSelectedIds,
        selectedTransactions: newSelectedTransactions,
    };
}), on(INVOICE_ACTIONS.setInvoiceFilters, (state, action) => ({
    ...state,
    loading: true,
    filters: action.filters,
})), on(INVOICE_ACTIONS.setInvoiceSortOrder, (state, action) => ({
    ...state,
    sort: action.sortOrder,
})), on(INVOICE_ACTIONS.updateInvoiceAfterPay, (state, action) => {
    let updatedTransactions = {};
    state.selectedIds.forEach((id) => {
        let transaction = state.selectedTransactions[id];
        const paymentInvoice = action.paymentInvoices.find((pi) => !!pi.errorCode &&
            // eslint-disable-next-line no-underscore-dangle
            (pi.invoiceID === transaction._id ||
                Object.values(state.translatedIds).indexOf(pi.invoiceID) >= 0));
        if (paymentInvoice) {
            transaction = {
                ...transaction,
                ...{ hasError: true, errorCode: paymentInvoice.errorCode },
            };
        }
        updatedTransactions = {
            ...updatedTransactions,
            // eslint-disable-next-line no-underscore-dangle
            ...{ [transaction._id]: transaction },
        };
    });
    return {
        ...state,
        postingDate: action.postingDate,
        selectedTransactions: updatedTransactions,
    };
}), on(INVOICE_ACTIONS.updateInvoice, (state, action) => {
    const initialSelectedTransactions = {
        ...state.initialSelectedTransactions,
    };
    const transaction = state.entities[action.invoiceId] ||
        state.selectedTransactions[action.invoiceId] ||
        initialSelectedTransactions[action.invoiceId];
    const updatedTransaction = {
        ...transaction,
        ...action.data,
    };
    const selectedTransactions = state.selectedTransactions[action.invoiceId]
        ? {
            ...state.selectedTransactions,
            ...{ [action.invoiceId]: updatedTransaction },
        }
        : state.selectedTransactions;
    const entities = { ...state.entities };
    if (Object.keys(entities).includes(action.invoiceId)) {
        entities[action.invoiceId] = updatedTransaction;
    }
    if (Object.keys(initialSelectedTransactions).includes(action.invoiceId)) {
        initialSelectedTransactions[action.invoiceId] = updatedTransaction;
    }
    return {
        ...state,
        entities,
        initialSelectedTransactions,
        selectedTransactions,
    };
}), on(INVOICE_ACTIONS.setInitialSelectedInvoices, (state, _action) => {
    const initialSelectedTransactions = { ...state.selectedTransactions };
    return {
        ...state,
        initialSelectedTransactions,
    };
}));
// eslint-disable-next-line prefer-arrow/prefer-arrow-functions
function invoicesReducer(state, action) {
    return reducer$b(state, action);
}

const keys = [
    'selectedIds',
    'selectedTransactions',
    'translatedIds',
    'postingDate',
];
const PAYMENT_INVOICE_CONFIG_TOKEN = new InjectionToken(PAYMENT_INVOICE);
const sessionStorageSyncReducer = (reducer) => (state, action) => {
    const nextState = reducer(state, action);
    switch (action.type) {
        case INVOICE_ACTIONS.clearSelectedInvoices.type:
        case INVOICE_ACTIONS.selectInvoices.type:
        case INVOICE_ACTIONS.updateInvoiceAfterPay.type:
        case INVOICE_ACTIONS.updateInvoice.type:
            keys.forEach((key) => {
                sessionStorage.setItem(key, JSON.stringify(nextState[key]));
            });
            break;
    }
    return nextState;
};
const invoiceStorageMetaReducers = [
    sessionStorageSyncReducer,
];
const paymentInvoiceConfigFactory = () => {
    const initialState = { ...initialInvoices };
    keys.forEach((key) => {
        const data = sessionStorage.getItem(key);
        if (!!data) {
            const raw = JSON.parse(data);
            initialState[key] = raw;
        }
    });
    return {
        initialState,
        metaReducers: invoiceStorageMetaReducers,
    };
};

const initialAccountSummary = {
    loading: false,
    error: null,
    totalBalance: 0,
    totalBalanceDue: 0,
};
const reducer$a = createReducer(initialAccountSummary, on(ACCOUNT_SUMMARY_ACTIONS.getAccountSummary, (_state, _action) => ({
    ...initialAccountSummary,
    loading: true,
})), on(ACCOUNT_SUMMARY_ACTIONS.getAccountSummarySuccess, (_state, action) => ({
    ...action.accountSummary,
    loading: false,
    error: null,
})), on(ACCOUNT_SUMMARY_ACTIONS.getAccountSummaryFail, (_state, action) => ({
    ...initialAccountSummary,
    loading: false,
    error: action.error,
})));
// eslint-disable-next-line prefer-arrow/prefer-arrow-functions
function accountSummaryReducer(state, action) {
    return reducer$a(state, action);
}

const arStatementAdapter = createEntityAdapter({
    selectId: (arStatement) => arStatement.statementID,
});
const initialArStatements = arStatementAdapter.getInitialState({
    error: null,
    filters: [],
    loading: false,
    nextPage: false,
    pageNum: 0,
    sort: [],
    selectedIds: [],
});
const reducer$9 = createReducer(initialArStatements, on(AR_STATEMENT_ACTIONS.getArStatements, (state, action) => {
    if (action.pageNum === 1) {
        return {
            ...initialArStatements,
            filters: state.filters,
            loading: true,
            sort: state.sort,
        };
    }
    else {
        return {
            ...state,
            loading: true,
        };
    }
}), on(AR_STATEMENT_ACTIONS.getArStatementsSuccess, (state, action) => {
    if (action.pageNum === 1) {
        return arStatementAdapter.setAll(action.arStatements ?? [], {
            ...state,
            error: null,
            loading: false,
            nextPage: action.nextPage,
            pageNum: action.pageNum,
        });
    }
    else {
        return arStatementAdapter.addMany(action.arStatements, {
            ...state,
            error: null,
            loading: false,
            nextPage: action.nextPage,
            pageNum: action.pageNum,
        });
    }
}), on(AR_STATEMENT_ACTIONS.getArStatementsFail, (state, action) => ({
    ...state,
    error: action.error,
    loading: false,
})), on(AR_STATEMENT_ACTIONS.setArStatementsFilters, (state, action) => ({
    ...state,
    filters: action.filters,
    loading: true,
})), on(AR_STATEMENT_ACTIONS.setArStatementsSortOrder, (state, action) => ({
    ...state,
    sort: action.sortOrder,
})), on(AR_STATEMENT_ACTIONS.selectStatements, (state, action) => {
    let newSelectedIds = [...state.selectedIds];
    action.statementsSelected.map((id) => {
        const wasSelected = newSelectedIds.includes(id);
        if (!wasSelected) {
            newSelectedIds = [...newSelectedIds, id];
        }
        else {
            newSelectedIds.splice(newSelectedIds.indexOf(id), 1);
        }
    });
    return { ...state, selectedIds: newSelectedIds };
}), on(AR_STATEMENT_ACTIONS.clearSelectedInvoices, (state, _) => ({
    ...state,
    selectedIds: [],
})));
// eslint-disable-next-line prefer-arrow/prefer-arrow-functions
function arStatementsReducer(state, action) {
    return reducer$9(state, action);
}

const initialAchFormState = {
    loading: false,
    error: null,
    encryptionKey: '',
    achAuditID: '',
};
const reducer$8 = createReducer(initialAchFormState, on(ACH_FORM_ACTIONS.getGeneratedACHForm, (state, _action) => ({
    ...initialAchFormState,
    loading: true,
})), on(ACH_FORM_ACTIONS.getGeneratedACHFormSuccess, (state, _action) => ({
    ...state,
    encryptionKey: _action.encryptionKey,
    achAuditID: _action.achAuditID,
    loading: false,
    error: null,
})), on(ACH_FORM_ACTIONS.getGeneratedACHFormFail, (state, _action) => ({
    ...state,
    loading: false,
    error: _action.error,
})));
// eslint-disable-next-line prefer-arrow/prefer-arrow-functions
function achFormReducer(state, action) {
    return reducer$8(state, action);
}

const initialStatusSummary$2 = {
    error: null,
    loading: false,
    totalOpenInvoices: 0,
};
const reducer$7 = createReducer(initialStatusSummary$2, on(OPEN_INVOICES_ACTIONS.getOpenInvoices, (_state, _action) => ({
    ...initialStatusSummary$2,
    loading: true,
})), on(OPEN_INVOICES_ACTIONS.getOpenInvoicesSuccess, (_state, action) => ({
    totalOpenInvoices: action.totalOpenInvoices,
    loading: false,
    error: null,
})), on(OPEN_INVOICES_ACTIONS.getOpenInvoicesFail, (_state, action) => ({
    ...initialStatusSummary$2,
    loading: false,
    error: action.error,
})));
// eslint-disable-next-line prefer-arrow/prefer-arrow-functions
function openInvoicesReducer(state, action) {
    return reducer$7(state, action);
}

const initialPayment$1 = {
    loading: false,
    error: null,
    paymentActivities: [],
    hasNextPage: false,
    pageNum: 0,
    filters: [],
    sort: [],
    warning: null,
};
const reducer$6 = createReducer(initialPayment$1, on(PAYMENT_ACTIONS.getPaymentActivity, (state, _action) => ({
    ...state,
    loading: true,
})), on(PAYMENT_ACTIONS.getPaymentActivitySuccess, (state, action) => ({
    ...state,
    loading: false,
    error: null,
    paymentActivities: action.pageNum === 1
        ? action.paymentActivities
        : [...state.paymentActivities, ...action.paymentActivities],
    hasNextPage: action.nextPage,
    pageNum: action.pageNum,
    warning: action.warning,
})), on(PAYMENT_ACTIONS.getPaymentActivityFail, (state, action) => ({
    ...state,
    loading: false,
    error: action.error,
})), on(PAYMENT_ACTIONS.setPaymentActivitySortOrder, (state, action) => ({
    ...state,
    loading: true,
    sort: action.sortOrder,
})), on(PAYMENT_ACTIONS.setPaymentActivityFilters, (state, action) => ({
    ...state,
    filters: action.filters,
})));
// eslint-disable-next-line prefer-arrow/prefer-arrow-functions
function paymentActivityReducer(state, action) {
    return reducer$6(state, action);
}

const initialPaymentCustomer = {
    customers: [],
};
const reducer$5 = createReducer(initialPaymentCustomer, on(PAYMENT_CUSTOMER_ACTIONS.customerUpdated, (_state, action) => ({
    customers: action.customers,
})));
// eslint-disable-next-line prefer-arrow/prefer-arrow-functions
function paymentCustomerReducer(state, action) {
    return reducer$5(state, action);
}

const initialPaymentMetric = {
    loading: false,
    loadingMetricDetails: false,
    loadingMetricSummary: false,
    customerMetricsError: null,
    metricSummaryError: null,
    customerMetrics: [],
    metricSummary: {
        creditHolds: 0,
        pastTerms: 0,
        closeToTerms: 0,
        currentPercentage: 0,
        overDueInvoicesTotal: 0,
        scheduledPaymentsTotal: 0,
        unusedCreditsTotal: 0,
        notAttachedCustomers: [],
    },
};
const reducer$4 = createReducer(initialPaymentMetric, on(PAYMENT_METRIC_ACTIONS.getMetricSummary, (state, _action) => ({
    ...state,
    loading: true,
    loadingMetricSummary: true,
})), on(PAYMENT_METRIC_ACTIONS.getMetricSummarySuccess, (state, action) => {
    const { type, ...metricSummary } = action;
    return {
        ...state,
        loading: !!state.loadingMetricDetails,
        loadingMetricSummary: false,
        metricSummaryError: null,
        metricSummary,
    };
}), on(PAYMENT_METRIC_ACTIONS.getMetricSummaryFail, (state, action) => ({
    ...state,
    loading: false,
    loadingMetricSummary: false,
    metricSummaryError: action.error,
})), on(PAYMENT_METRIC_ACTIONS.getMetricDetails, (state, _) => ({
    ...state,
    loading: true,
    loadingMetricDetails: true,
})), on(PAYMENT_METRIC_ACTIONS.getMetricDetailsSuccess, (state, action) => ({
    ...state,
    loading: !!state.loadingMetricSummary,
    loadingMetricDetails: false,
    customerMetricsError: null,
    customerMetrics: action.customerMetrics,
})), on(PAYMENT_METRIC_ACTIONS.getMetricDetailsFail, (state, action) => ({
    ...state,
    loading: false,
    loadingMetricDetails: false,
    customerMetricsError: action.error,
})));
// eslint-disable-next-line prefer-arrow/prefer-arrow-functions
function paymentMetricReducer(state, action) {
    return reducer$4(state, action);
}

const initialStatusSummary$1 = {
    error: null,
    loading: false,
    totalOverDue: 0,
    totalUnpaid: 0,
    totalPending: 0,
    totalPaid: 0,
    totalPartialPaid: 0,
    totalProcessing: 0,
    totalApplied: 0,
    totalUnused: 0,
    totalCancelling: 0,
    totalCancelled: 0,
    totalAutopay: 0,
};
const reducer$3 = createReducer(initialStatusSummary$1, on(STATUS_SUMMARY_ACTIONS.getPaymentActivityStatusSummary, (_state, _action) => ({
    ...initialStatusSummary$1,
    loading: true,
})), on(STATUS_SUMMARY_ACTIONS.getPaymentActivityStatusSummarySuccess, (_state, action) => ({
    ...action.statusSummary,
    loading: false,
    error: null,
})), on(STATUS_SUMMARY_ACTIONS.getPaymentActivityStatusSummaryFail, (_state, action) => ({
    ...initialStatusSummary$1,
    loading: false,
    error: action.error,
})));
// eslint-disable-next-line prefer-arrow/prefer-arrow-functions
function paymentActivityStatusSummaryReducer(state, action) {
    return reducer$3(state, action);
}

const initialPayment = {
    loading: false,
    error: null,
    customerPayments: [],
    scheduledPaymentDate: 0,
    postingDate: 0,
    paymentDetails: undefined,
};
const reducer$2 = createReducer(initialPayment, on(PAYMENT_ACTIONS.clearError, (state, _action) => ({
    ...state,
    error: null,
})), on(PAYMENT_ACTIONS.clearPayments, (state, _action) => ({
    ...state,
    customerPayments: [],
})), on(PAYMENT_ACTIONS.clearPaymentDetails, (state, _action) => ({
    ...state,
    paymentDetails: undefined,
})), on(PAYMENT_ACTIONS.submitPayment, (_state, _action) => ({
    ...initialPayment,
    loading: true,
})), on(PAYMENT_ACTIONS.submitPaymentSuccess, (state, action) => ({
    ...state,
    customerPayments: action.customerPayments,
    scheduledPaymentDate: action.scheduledPaymentDate,
    postingDate: action.postingDate,
    loading: false,
    error: null,
})), on(PAYMENT_ACTIONS.submitPaymentFail, (_state, action) => ({
    ...initialPayment,
    loading: false,
    error: action.error,
})), on(PAYMENT_ACTIONS.getPaymentDetails, (state, _action) => ({
    ...state,
    loading: true,
})), on(PAYMENT_ACTIONS.getPaymentDetailsSuccess, (state, action) => ({
    ...state,
    paymentDetails: action.paymentDetails,
    loading: false,
    error: null,
})), on(PAYMENT_ACTIONS.getPaymentDetailsFail, (state, action) => ({
    ...state,
    loading: false,
    error: action.error,
})), on(PAYMENT_ACTIONS.cancelPayment, (state, _action) => ({
    ...state,
    loading: true,
})), on(PAYMENT_ACTIONS.cancelPaymentSuccess, (state, _action) => ({
    ...state,
    loading: false,
    error: null,
    cancellingFlag: true,
})), on(PAYMENT_ACTIONS.cancelPaymentFail, (state, action) => ({
    ...state,
    loading: false,
    error: action.error,
})), on(PAYMENT_ACTIONS.clearCancellingFlag, (state, _action) => ({
    ...state,
    cancellingFlag: false,
})));
// eslint-disable-next-line prefer-arrow/prefer-arrow-functions
function paymentReducer(state, action) {
    return reducer$2(state, action);
}

const recentCreditAdapter = createEntityAdapter({
    // eslint-disable-next-line no-underscore-dangle
    selectId: (transaction) => transaction._id,
});
const initialRecentCredits = recentCreditAdapter.getInitialState({
    loading: false,
    error: null,
});
const reducer$1 = createReducer(initialRecentCredits, on(RECENT_CREDIT_ACTIONS.getRecentCredits, (_state, _action) => ({
    ...initialRecentCredits,
    loading: true,
})), on(RECENT_CREDIT_ACTIONS.getRecentCreditsSuccess, (state, action) => recentCreditAdapter.setAll(action.invoices, {
    ...initialRecentCredits,
    loading: false,
    error: null,
})), on(RECENT_CREDIT_ACTIONS.getRecentCreditsFail, (_state, action) => ({
    ...initialRecentCredits,
    loading: false,
    error: action.error,
})));
// eslint-disable-next-line prefer-arrow/prefer-arrow-functions
function recentCreditsReducer(state, action) {
    return reducer$1(state, action);
}

const initialStatusSummary = {
    error: null,
    loading: false,
    totalOverDue: 0,
    totalUnpaid: 0,
    totalPending: 0,
    totalPaid: 0,
    totalPartialPaid: 0,
    totalProcessing: 0,
    totalApplied: 0,
    totalUnused: 0,
    totalCancelling: 0,
    totalCancelled: 0,
    totalAutopay: 0,
};
const reducer = createReducer(initialStatusSummary, on(STATUS_SUMMARY_ACTIONS.getStatusSummary, (_state, _action) => ({
    ...initialStatusSummary,
    loading: true,
})), on(STATUS_SUMMARY_ACTIONS.getStatusSummarySuccess, (_state, action) => ({
    ...action.statusSummary,
    loading: false,
    error: null,
})), on(STATUS_SUMMARY_ACTIONS.getStatusSummaryFail, (_state, action) => ({
    ...initialStatusSummary,
    loading: false,
    error: action.error,
})));
// eslint-disable-next-line prefer-arrow/prefer-arrow-functions
function statusSummaryReducer(state, action) {
    return reducer(state, action);
}

class PaymentStateService {
    constructor(store, panAppState) {
        this.store = store;
        this.panAppState = panAppState;
    }
    initializeStore() {
        this.panAppState.customer$
            .pipe(withLatestFrom(this.panAppState.feature$(['split_user_payments_link'])), filter(([customer, feature]) => !!customer && !!customer.customerName && feature))
            .subscribe(([customer, feature]) => {
            if (feature) {
                this.store.dispatch(PAYMENT_CUSTOMER_ACTIONS.customerUpdated({
                    customers: [customer.customerNumber],
                }));
            }
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: PaymentStateService, deps: [{ token: i1.Store }, { token: i2.PanAppState }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: PaymentStateService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: PaymentStateService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.Store }, { type: i2.PanAppState }]; } });

class NgRxPaymentModule {
    constructor(ngRxPaymentLibConfig) {
        this.ngRxPaymentLibConfig = ngRxPaymentLibConfig;
    }
    static forRoot(ngRxPaymentLibConfig) {
        return {
            ngModule: NgRxPaymentModule,
            providers: [
                {
                    provide: NGRX_PAYMENT_LIB_CONFIG,
                    useValue: ngRxPaymentLibConfig,
                },
                {
                    provide: APP_INITIALIZER,
                    useFactory: initializePaymentState,
                    deps: [PaymentStateService],
                    multi: true,
                },
            ],
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: NgRxPaymentModule, deps: [{ token: NGRX_PAYMENT_LIB_CONFIG }], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.2.12", ngImport: i0, type: NgRxPaymentModule, imports: [i1.StoreFeatureModule, i1.StoreFeatureModule, i1.StoreFeatureModule, i1.StoreFeatureModule, i1.StoreFeatureModule, i1.StoreFeatureModule, i1.StoreFeatureModule, i1.StoreFeatureModule, i1.StoreFeatureModule, i1.StoreFeatureModule, i1.StoreFeatureModule, i1.StoreFeatureModule, i1.StoreFeatureModule, i1$2.EffectsFeatureModule] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: NgRxPaymentModule, providers: [
            PaymentStateService,
            {
                provide: PAYMENT_BANK_DETAIL_CONFIG_TOKEN,
                useFactory: paymentBankDetailConfigFactory,
            },
            {
                provide: PAYMENT_INVOICE_CONFIG_TOKEN,
                useFactory: paymentInvoiceConfigFactory,
            },
        ], imports: [StoreModule.forFeature(PAYMENT_ACCOUNT_SUMMARY, accountSummaryReducer),
            StoreModule.forFeature(PAYMENT_AR_STATEMENT, arStatementsReducer),
            StoreModule.forFeature(PAYMENT_BANK_DETAILS, bankReducer, PAYMENT_BANK_DETAIL_CONFIG_TOKEN),
            StoreModule.forFeature(PAYMENT_ACH_FORM, achFormReducer),
            StoreModule.forFeature(PAYMENT_OPEN_INVOICES, openInvoicesReducer),
            StoreModule.forFeature(PAYMENT_CUSTOMER_KEY, paymentCustomerReducer),
            StoreModule.forFeature(PAYMENT_INVOICE, invoicesReducer, PAYMENT_INVOICE_CONFIG_TOKEN),
            StoreModule.forFeature(PAYMENT_RECENT_CREDIT, recentCreditsReducer),
            StoreModule.forFeature(PAYMENT_STATUS_SUMMARY, statusSummaryReducer),
            StoreModule.forFeature(PAYMENT_ACTIVITY_STATUS_SUMMARY, paymentActivityStatusSummaryReducer),
            StoreModule.forFeature(PAYMENT_CURRENT, paymentReducer),
            StoreModule.forFeature(PAYMENT_ACTIVITY, paymentActivityReducer),
            StoreModule.forFeature(PAYMENT_METRIC, paymentMetricReducer),
            EffectsModule.forFeature([
                AccountSummaryEffects,
                ArStatementEffects,
                InvoiceEffects,
                BankEffects,
                AchFormEffects,
                OpenInvoicesEffects,
                PaymentEffects,
                RecentCreditEffects,
                StatusSummaryEffects,
                PaymentMetricEffects,
                ShareEmailEffects,
            ])] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: NgRxPaymentModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [],
                    imports: [
                        StoreModule.forFeature(PAYMENT_ACCOUNT_SUMMARY, accountSummaryReducer),
                        StoreModule.forFeature(PAYMENT_AR_STATEMENT, arStatementsReducer),
                        StoreModule.forFeature(PAYMENT_BANK_DETAILS, bankReducer, PAYMENT_BANK_DETAIL_CONFIG_TOKEN),
                        StoreModule.forFeature(PAYMENT_ACH_FORM, achFormReducer),
                        StoreModule.forFeature(PAYMENT_OPEN_INVOICES, openInvoicesReducer),
                        StoreModule.forFeature(PAYMENT_CUSTOMER_KEY, paymentCustomerReducer),
                        StoreModule.forFeature(PAYMENT_INVOICE, invoicesReducer, PAYMENT_INVOICE_CONFIG_TOKEN),
                        StoreModule.forFeature(PAYMENT_RECENT_CREDIT, recentCreditsReducer),
                        StoreModule.forFeature(PAYMENT_STATUS_SUMMARY, statusSummaryReducer),
                        StoreModule.forFeature(PAYMENT_ACTIVITY_STATUS_SUMMARY, paymentActivityStatusSummaryReducer),
                        StoreModule.forFeature(PAYMENT_CURRENT, paymentReducer),
                        StoreModule.forFeature(PAYMENT_ACTIVITY, paymentActivityReducer),
                        StoreModule.forFeature(PAYMENT_METRIC, paymentMetricReducer),
                        EffectsModule.forFeature([
                            AccountSummaryEffects,
                            ArStatementEffects,
                            InvoiceEffects,
                            BankEffects,
                            AchFormEffects,
                            OpenInvoicesEffects,
                            PaymentEffects,
                            RecentCreditEffects,
                            StatusSummaryEffects,
                            PaymentMetricEffects,
                            ShareEmailEffects,
                        ]),
                    ],
                    providers: [
                        PaymentStateService,
                        {
                            provide: PAYMENT_BANK_DETAIL_CONFIG_TOKEN,
                            useFactory: paymentBankDetailConfigFactory,
                        },
                        {
                            provide: PAYMENT_INVOICE_CONFIG_TOKEN,
                            useFactory: paymentInvoiceConfigFactory,
                        },
                    ],
                    exports: [],
                }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [NGRX_PAYMENT_LIB_CONFIG]
                }] }]; } });
// eslint-disable-next-line prefer-arrow/prefer-arrow-functions
function initializePaymentState(paymentState) {
    const state = () => paymentState.initializeStore();
    return state;
}

/*
 * Public API Surface of ngrx-payment
 */

/**
 * Generated bundle index. Do not edit.
 */

export { AccountSummaryStoreService, AchFormStoreService, ArStatementStoreService, BankStoreService, InvoiceFilterIds, InvoiceStoreService, NgRxPaymentModule, PaymentCustomerStoreService, PaymentStoreService, RecentCreditStoreService, SharePaymentsEmailStoreService, SortDirection, accountSummarySelectors, achFormSelectors, arStatementSelectors, bankSelectors, invoicesSelectors, openInvoicesSelectors, paymentCustomerSelectors, paymentSelectors, recentCreditSelectors, statusSummarySelectors };
